import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { uploadToCloudinary } from "@/lib/cloudinary";
import { PageHeader } from "@/components/PageHeader";
import LearnerHint from "@/components/LearnerHint";
import PagosPresupuestoPanel from "@/components/PagosPresupuestoPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import HistorialVistasPresupuesto from "@/components/HistorialVistasPresupuesto";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import CalcMaterialPanel from "@/components/CalcMaterialPanel";
import useDropImage from "@/hooks/useDropImage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Trash2, FileText, Download, MessageCircle, Send,
  Image as ImageIcon, Paperclip, Search, Building2, ExternalLink,
  RefreshCw, Save, Calculator, Copy, QrCode, Link2, Globe, Upload, Eye,
  UserPlus, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { useEntityRefresh } from "@/lib/useEntityRefresh";
import { useAuth } from "@/context/AuthContext";
import { fmtEUR } from "@/lib/format";
import { PartidasAprobacionPanel } from "@/components/PartidasAprobacion";

const ESTADOS = {
  borrador:   { label: "Borrador",   color: "bg-zinc-700 text-zinc-200" },
  enviado:    { label: "Enviado",    color: "bg-blue-600/25 text-blue-200 border border-blue-500/40" },
  aceptado:   { label: "Aceptado",   color: "bg-emerald-600/25 text-emerald-200 border border-emerald-500/40" },
  rechazado:  { label: "Rechazado",  color: "bg-red-600/25 text-red-200 border border-red-500/40" },
  caducado:   { label: "Caducado",   color: "bg-zinc-600/25 text-zinc-300 border border-zinc-500/40" },
  anulado_por_cliente: { label: "Anulado · Express", color: "bg-fuchsia-600/25 text-fuchsia-200 border border-fuchsia-500/40" },
};

const CATEGORIAS = [
  { v: "produccion", l: "Producción", color: "bg-blue-600/20 text-blue-200" },
  { v: "montaje",    l: "Montaje",    color: "bg-emerald-600/20 text-emerald-200" },
  { v: "diseno",     l: "Diseño",     color: "bg-violet-600/20 text-violet-200" },
  { v: "externo",    l: "Externo",    color: "bg-orange-600/20 text-orange-200" },
  { v: "otros",      l: "Otros",      color: "bg-zinc-600/30 text-zinc-200" },
];


function superficieM2(it) {
  if (it.base_cm && it.altura_cm) {
    return (parseFloat(it.base_cm) / 100) * (parseFloat(it.altura_cm) / 100);
  }
  return null;
}

function cantidadEfectiva(it) {
  const qty = parseFloat(it.cantidad) || 0;
  const unidad = (it.unidad || "").toLowerCase();
  if (unidad === "m2") {
    const m2 = superficieM2(it);
    if (m2) return m2 * qty;
  }
  if (unidad === "ml") {
    const base = parseFloat(it.base_cm || it.altura_cm || 0);
    if (base) return (base / 100) * qty;
  }
  return qty;
}

function calcItem(it) {
  const precio = parseFloat(it.precio_unitario) || 0;
  const desc = parseFloat(it.descuento_pct) || 0;
  const bruto = cantidadEfectiva(it) * precio;
  return bruto * (1 - desc / 100);
}

function calcTotales(items, ivaPct = 21) {
  const subtotal = (items || []).reduce((acc, it) => acc + calcItem(it), 0);
  const iva = subtotal * ivaPct / 100;
  return { subtotal, iva, total: subtotal + iva };
}

export default function PresupuestoDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [empresa, setEmpresa] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [genPdf, setGenPdf] = useState(false);
  const [openAddItem, setOpenAddItem] = useState(false);
  const [openProveedor, setOpenProveedor] = useState(null); // item id
  // Calculadora gráfica: item id activo + materiales (lazy-load la primera vez)
  const [openCalc, setOpenCalc] = useState(null);
  const [materiales, setMateriales] = useState(null);  // null = no cargado todavía
  const [openWa, setOpenWa] = useState(false);
  const [waState, setWaState] = useState({ telefono: "", mensaje: "", portal_url: "", pay_url: "" });
  // IA — Parseo presupuesto proveedor
  const [openIA, setOpenIA] = useState(false);
  const [iaTexto, setIaTexto] = useState("");
  const [iaLoading, setIaLoading] = useState(false);
  const [iaPreview, setIaPreview] = useState(null);
  const [iaModo, setIaModo] = useState("pdf"); // 'pdf' | 'texto'
  const [iaFile, setIaFile] = useState(null);
  const [iaDragOver, setIaDragOver] = useState(false);
  const iaFileInputRef = useRef(null);
  const fileRefImg = useRef({}); // por item id
  const fileRefAdj = useRef(null);
  const fileRefArchCli = useRef(null);  // archivos para el cliente (portal)
  const [chatMsg, setChatMsg] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const [convertir, setConvertir] = useState(false);
  const [openCliCombo, setOpenCliCombo] = useState(false);
  // Crear cliente nuevo desde el presupuesto
  const [openNuevoCli, setOpenNuevoCli] = useState(false);
  const [nuevoCli, setNuevoCli] = useState({ nombre: "", cif: "", direccion_fiscal: "", telefono: "", email_contacto: "", persona_contacto: "" });
  const [savingNuevoCli, setSavingNuevoCli] = useState(false);
  // Edición inline del snapshot del cliente en este presupuesto
  const [editSnapshot, setEditSnapshot] = useState(false);
  const [snapshotDraft, setSnapshotDraft] = useState(null);
  // Anular y derivar a Express (Flujo B · minimal-trace)
  const { hasRole } = useAuth();
  const puedeAnularDerivar = hasRole(["comercial"]);
  const [openAnular, setOpenAnular] = useState(false);
  const [anularBrief, setAnularBrief] = useState("");
  const [anularFecha, setAnularFecha] = useState("");
  const [anularMotivo, setAnularMotivo] = useState("");
  const [anulando, setAnulando] = useState(false);

  const portalUrl = useMemo(() => {
    if (!p?.portal_token) return null;
    // El portal /p/pres/* es una ruta de FRONTEND servida por el mismo SPA.
    // Usamos el origin del navegador (en producción = app.rotulaciondnf.com,
    // en preview = ...preview.emergentagent.com). Evitamos REACT_APP_BACKEND_URL
    // porque en producción apunta al host del backend (dnf-produccion.emergent.host),
    // que no es la URL "bonita" que queremos enseñar al cliente.
    const origin = (typeof window !== "undefined" && window.location?.origin) || "";
    const isBadHost = /(\.emergent\.host|\.emergentagent\.com|\.cluster-8\.deploy\.)/.test(origin);
    const base = (!origin || isBadHost) ? "https://app.rotulaciondnf.com" : origin;
    return `${base}/p/pres/${p.portal_token}`;
  }, [p?.portal_token]);

  const activarPortal = async () => {
    try {
      const { data } = await api.post(`/presupuestos/${id}/portal/activar`);
      setP({ ...p, portal_token: data.token, portal_activado_at: new Date().toISOString() });
      toast.success("Portal del cliente activado");
    } catch (e) { toast.error("Error: " + (e.message || e)); }
  };

  const copiarUrlPortal = async () => {
    if (!portalUrl) return;
    try {
      await navigator.clipboard.writeText(portalUrl);
      toast.success("URL del portal copiada");
    } catch (e) { toast.error("No se pudo copiar"); }
  };

  const subirArchivoParaCliente = async (file) => {
    if (!file) return;
    try {
      const up = await uploadToCloudinary(file, "dnf/portal-presupuestos-dnf", "auto");
      const { data } = await api.post(`/presupuestos/${id}/portal/archivos-para-cliente`, {
        nombre: file.name,
        url: up.secure_url,
        tipo: file.type || up.resource_type,
        tamano: file.size,
        public_id: up.public_id,
      });
      setP({ ...p, archivos_para_cliente: [...(p.archivos_para_cliente || []), data] });
      toast.success("Archivo subido al portal del cliente");
    } catch (e) { toast.error("Error: " + (e.message || e)); }
  };

  const borrarArchivoParaCliente = async (aid) => {
    if (!window.confirm("¿Quitar este archivo del portal?")) return;
    await api.delete(`/presupuestos/${id}/portal/archivos-para-cliente/${aid}`);
    setP({ ...p, archivos_para_cliente: (p.archivos_para_cliente || []).filter((a) => a.id !== aid) });
  };

  // ====== Chat ======
  const enviarChat = async () => {
    const txt = chatMsg.trim();
    if (!txt) return;
    setSendingChat(true);
    try {
      const { data } = await api.post(`/presupuestos/${id}/mensajes`, { texto: txt });
      setP((prev) => prev ? { ...prev, mensajes: [...(prev.mensajes || []), data] } : prev);
      setChatMsg("");
    } catch (e) {
      toast.error("Error enviando mensaje");
    } finally { setSendingChat(false); }
  };

  // Auto-refresh suave del presupuesto cada 20s para detectar nuevos mensajes / decisiones del cliente
  useEffect(() => {
    if (!id) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/presupuestos/${id}`);
        setP((prev) => {
          if (!prev) return data;
          // Mantenemos ediciones locales de campos en curso (notas, condiciones)
          // pero refrescamos campos "del lado cliente"
          return {
            ...prev,
            mensajes: data.mensajes || prev.mensajes,
            archivos_cliente: data.archivos_cliente || prev.archivos_cliente,
            cliente_decision: data.cliente_decision,
            cliente_decision_at: data.cliente_decision_at,
            cliente_comentario: data.cliente_comentario,
            items_seleccionados: data.items_seleccionados,
            estado: data.estado,
            portal_visitas: data.portal_visitas,
            portal_visto_at: data.portal_visto_at,
            portal_ultima_visita_at: data.portal_ultima_visita_at,
            portal_historial_vistas: data.portal_historial_vistas,
          };
        });
      } catch (_) { /* silencioso */ }
    }, 20000);
    return () => clearInterval(t);
  }, [id]);

  // ====== Convertir a petición ======
  const convertirAPeticion = async () => {
    if (!window.confirm("¿Convertir este presupuesto en una Petición con sus líneas y componentes?\nEsto creará una petición en el módulo de Operaciones.")) return;
    setConvertir(true);
    try {
      const { data } = await api.post(`/presupuestos/${id}/convertir-peticion`);
      toast.success(`Petición creada con ${data.lineas_creadas} líneas. Abriendo…`);
      setP({ ...p, peticion_id: data.peticion_id });
      setTimeout(() => nav(`/peticiones/${data.peticion_id}`), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error convirtiendo a petición");
    } finally { setConvertir(false); }
  };

  // ─── Anular y derivar a Express ───────────────────────────────────────
  // Genera un brief técnico mínimo a partir de los items (sin precios, sin cliente).
  const briefDesdeItems = (items) => {
    if (!Array.isArray(items) || items.length === 0) return "";
    const MAX = 12;
    const lineas = [];
    for (const it of items.slice(0, MAX)) {
      const desc = (it.descripcion || "").trim();
      if (!desc) continue;
      const dim = [];
      if (it.base_cm && it.altura_cm) {
        dim.push(`${Number(it.base_cm)}×${Number(it.altura_cm)} cm`);
      } else if (it.base_cm) {
        dim.push(`${Number(it.base_cm)} cm`);
      }
      if (it.cantidad) {
        const q = Number(it.cantidad);
        const qt = Number.isInteger(q) ? `${q}` : `${q}`;
        dim.push(`${qt} ${(it.unidad || "").trim()}`.trim());
      }
      const sufijo = dim.length ? ` (${dim.join(", ")})` : "";
      lineas.push(`- ${desc.slice(0, 120)}${sufijo}`);
    }
    if (items.length > MAX) lineas.push(`- (+${items.length - MAX} líneas más)`);
    return lineas.join("\n");
  };

  const abrirAnularDerivar = () => {
    setAnularBrief(briefDesdeItems(p?.items || []));
    setAnularFecha("");
    setAnularMotivo("");
    setOpenAnular(true);
  };

  const confirmarAnularDerivar = async () => {
    setAnulando(true);
    try {
      const { data } = await api.post(`/presupuestos/${id}/anular-derivar`, {
        brief: anularBrief || null,
        fecha_montaje: anularFecha || null,
        motivo: anularMotivo || null,
      });
      toast.success(`Presupuesto anulado · ${data.express_ref} creado`);
      setOpenAnular(false);
      // Recargar el presupuesto para reflejar el nuevo estado y campos
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error anulando y derivando");
    } finally {
      setAnulando(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [pres, cli, cat, emp] = await Promise.all([
        api.get(`/presupuestos/${id}`),
        api.get("/clientes"),
        api.get("/catalogo-precios"),
        api.get("/presupuestos-config/empresa").catch(() => ({ data: {} })),
      ]);
      setP(pres.data);
      setClientes(cli.data);
      setCatalogo(cat.data || []);
      setEmpresa(emp.data || {});
    } catch (e) {
      toast.error("No se pudo cargar el presupuesto");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Iter 55: refresh automático cuando otra pestaña crea/edita un cliente
  useEntityRefresh("cliente", () => {
    api.get("/clientes").then((r) => setClientes(r.data || [])).catch(() => {});
  });

  // patch helper
  const patchPres = async (updates) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/presupuestos/${id}`, updates);
      setP(data);
    } catch (e) {
      toast.error("Error guardando");
    } finally { setSaving(false); }
  };

  const cambiarCliente = async (cid) => {
    await patchPres({ cliente_id: cid });
  };

  const crearClienteRapido = async () => {
    const nombre = (nuevoCli.nombre || "").trim();
    if (!nombre) { toast.error("Indica al menos el nombre"); return; }
    setSavingNuevoCli(true);
    try {
      const { data: cli } = await api.post("/clientes", nuevoCli);
      setClientes((prev) => [...prev, cli]);
      await cambiarCliente(cli.id);
      toast.success(`Cliente "${cli.nombre}" creado y asignado`);
      setOpenNuevoCli(false);
      setNuevoCli({ nombre: "", cif: "", direccion_fiscal: "", telefono: "", email_contacto: "", persona_contacto: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear el cliente");
    } finally { setSavingNuevoCli(false); }
  };

  const abrirEditarSnapshot = () => {
    setSnapshotDraft({
      razon_social: p?.cliente_snapshot?.razon_social || "",
      cif: p?.cliente_snapshot?.cif || "",
      direccion: p?.cliente_snapshot?.direccion || "",
      cp: p?.cliente_snapshot?.cp || "",
      ciudad: p?.cliente_snapshot?.ciudad || "",
      provincia: p?.cliente_snapshot?.provincia || "",
      email: p?.cliente_snapshot?.email || "",
      telefono: p?.cliente_snapshot?.telefono || "",
      contacto_nombre: p?.cliente_snapshot?.contacto_nombre || "",
      observaciones: p?.cliente_snapshot?.observaciones || "",
    });
    setEditSnapshot(true);
  };

  const guardarSnapshot = async () => {
    await patchPres({ cliente_snapshot: snapshotDraft });
    setEditSnapshot(false);
    toast.success("Datos del cliente actualizados (sólo en este presupuesto)");
  };

  // ====== Items ======
  const addItemDesdeCatalogo = async (cat) => {
    const it = {
      catalogo_id: cat.id,
      categoria: cat.categoria,
      descripcion: cat.nombre,
      unidad: cat.unidad,
      cantidad: 1,
      precio_unitario: cat.precio,
    };
    const { data } = await api.post(`/presupuestos/${id}/items`, it);
    setP({ ...p, items: [...(p.items || []), data] });
    setOpenAddItem(false);
  };

  const addItemLibre = async (categoria = "produccion") => {
    const it = { categoria, descripcion: "Nuevo item", unidad: "ud", cantidad: 1, precio_unitario: 0 };
    const { data } = await api.post(`/presupuestos/${id}/items`, it);
    setP({ ...p, items: [...(p.items || []), data] });
    setOpenAddItem(false);
  };

  const addItemExterno = async () => {
    const it = {
      categoria: "externo",
      descripcion: "Rótulo / letras / producción externa",
      unidad: "ud",
      cantidad: 1,
      precio_unitario: 0,
      proveedor_externo: { nombre: "", numero_presupuesto: "", precio_coste: 0, margen_pct: 30 },
    };
    const { data } = await api.post(`/presupuestos/${id}/items`, it);
    setP({ ...p, items: [...(p.items || []), data] });
    setOpenAddItem(false);
  };

  const updateItem = async (iid, updates) => {
    // Update local primero (UX)
    const newItems = (p.items || []).map((it) => it.id === iid ? { ...it, ...updates } : it);
    setP({ ...p, items: newItems });
    // Persist
    try {
      await api.patch(`/presupuestos/${id}/items/${iid}`, updates);
    } catch (e) {
      toast.error("Error guardando item");
    }
  };

  const deleteItem = async (iid) => {
    if (!window.confirm("¿Eliminar este item?")) return;
    await api.delete(`/presupuestos/${id}/items/${iid}`);
    setP({ ...p, items: (p.items || []).filter((it) => it.id !== iid) });
  };

  // Duplicar item: copia todos los campos relevantes y crea uno nuevo justo después.
  // Útil cuando el mismo material se aplica con varias medidas (vinilo impreso × 10 tamaños).
  const duplicarItem = async (iid) => {
    const orig = (p.items || []).find((x) => x.id === iid);
    if (!orig) return;
    const dup = {
      catalogo_id: orig.catalogo_id || null,
      categoria: orig.categoria,
      descripcion: orig.descripcion,
      unidad: orig.unidad,
      cantidad: orig.cantidad,
      precio_unitario: orig.precio_unitario,
      descuento_pct: orig.descuento_pct || 0,
      base_cm: orig.base_cm ?? null,
      altura_cm: orig.altura_cm ?? null,
      imagen_url: orig.imagen_url || null,
      imagen_public_id: orig.imagen_public_id || null,
      opcional: !!orig.opcional,
      grupo: orig.grupo || null,
      seleccionado_default: !!orig.seleccionado_default,
      proveedor_externo: orig.proveedor_externo ? { ...orig.proveedor_externo } : null,
    };
    try {
      const { data } = await api.post(`/presupuestos/${id}/items`, dup);
      // Insertar la copia justo después del original
      const items = [...(p.items || [])];
      const i = items.findIndex((x) => x.id === iid);
      const insertAt = i >= 0 ? i + 1 : items.length;
      items.splice(insertAt, 0, data);
      setP({ ...p, items });
      toast.success("Línea duplicada · cambia solo las medidas");
    } catch (e) {
      toast.error("No se pudo duplicar: " + (e.response?.data?.detail || e.message));
    }
  };

  // Abrir calculadora gráfica (carga materiales perezosamente la 1ª vez)
  const abrirCalc = async (iid) => {
    if (materiales === null) {
      try {
        const { data } = await api.get("/materiales");
        setMateriales(data || []);
      } catch (e) {
        setMateriales([]);
        toast.warning("No se pudo cargar el catálogo de materiales (puedes calcular igualmente metiendo ancho de bobina manual)");
      }
    }
    setOpenCalc(iid);
  };

  const subirImagenItem = async (iid, file) => {
    if (!file) return;
    if (file.size > 10_000_000) { toast.error("Imagen demasiado grande (máx 10MB)"); return; }
    try {
      const up = await uploadToCloudinary(file, "dnf/presupuestos-imgs", "image");
      await updateItem(iid, { imagen_url: up.secure_url, imagen_public_id: up.public_id });
      toast.success("Imagen añadida");
    } catch (e) {
      toast.error("Error subiendo imagen: " + (e.message || e));
    }
  };

  const subirAdjuntoProveedor = async (iid, file) => {
    if (!file) return;
    try {
      const up = await uploadToCloudinary(file, "dnf/presupuestos-proveedor", "auto");
      const it = (p.items || []).find((x) => x.id === iid);
      const prov = { ...(it.proveedor_externo || {}), adjunto_url: up.secure_url, adjunto_nombre: file.name, adjunto_public_id: up.public_id };
      await updateItem(iid, { proveedor_externo: prov });
      toast.success("Presupuesto proveedor adjuntado (interno)");
    } catch (e) {
      toast.error("Error subiendo: " + (e.message || e));
    }
  };

  const subirAdjuntoInterno = async (file) => {
    if (!file) return;
    try {
      const up = await uploadToCloudinary(file, "dnf/presupuestos-adjuntos", "auto");
      const body = {
        nombre: file.name,
        url: up.secure_url,
        tipo: file.type || up.resource_type,
        tamano: file.size,
        public_id: up.public_id,
      };
      const { data } = await api.post(`/presupuestos/${id}/adjuntos`, body);
      setP({ ...p, adjuntos_internos: [...(p.adjuntos_internos || []), data] });
      toast.success("Adjunto interno subido");
    } catch (e) {
      toast.error("Error: " + (e.message || e));
    }
  };

  const deleteAdjuntoInterno = async (aid) => {
    if (!window.confirm("¿Eliminar este adjunto interno?")) return;
    await api.delete(`/presupuestos/${id}/adjuntos/${aid}`);
    setP({ ...p, adjuntos_internos: (p.adjuntos_internos || []).filter((a) => a.id !== aid) });
  };

  // ====== Acciones ======
  const generarPDF = async () => {
    setGenPdf(true);
    try {
      // timeout explícito 60s para que nunca se quede colgado
      const { data } = await api.post(`/presupuestos/${id}/generar-pdf`, {}, { timeout: 60000 });
      setP((prev) => ({ ...prev, pdf_url: data.pdf_url, pdf_generated_at: new Date().toISOString() }));
      toast.success("✅ PDF generado correctamente. Pulsa 'Ver / Descargar' para abrirlo.");
      // Recargar el presupuesto para reflejar portal_token y otros campos derivados
      await load();
    } catch (e) {
      console.error("[generarPDF] error:", e);
      const msg = e?.response?.data?.detail || e?.message || "Error desconocido";
      toast.error("Error generando PDF: " + msg);
    } finally {
      setGenPdf(false);
    }
  };

  const abrirPDF = async () => {
    // Estrategia robusta para esquivar popup blockers y AdBlockers:
    // 1. Abrimos un tab nuevo INMEDIATAMENTE (en el hilo del click del usuario)
    // 2. Cargamos el PDF como blob y le asignamos la URL al tab ya abierto
    // Si window.open devuelve null (popup blocker), caemos a fallback con <a>.
    const win = window.open("", "_blank");
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("dnf_access_token");
      const r = await fetch(`${backend}/api/presupuestos/${id}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (win && !win.closed) {
        win.location.href = url;
      } else {
        // Fallback: forzar descarga vía <a> en el DOM
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.download = `${p.numero || "presupuesto"}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (win) win.close();
      // Último fallback: Cloudinary directo (si no hay adblocker)
      if (p.pdf_url) window.open(p.pdf_url, "_blank");
      else toast.error("No se pudo abrir el PDF: " + e.message);
    }
  };

  const abrirDialogoWhatsApp = async () => {
    try {
      // Pedimos al backend el mensaje pre-rellenado (con número del snapshot si lo hay).
      // Ya NO requiere PDF generado: el portal URL siempre está disponible.
      const { data } = await api.post(`/presupuestos/${id}/whatsapp-link`);
      setWaState({
        telefono: data.telefono || p.cliente_snapshot?.telefono || "",
        mensaje: data.mensaje || "",
        portal_url: data.portal_url || "",
      });
      setOpenWa(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error preparando WhatsApp");
    }
  };

  const abrirDialogoCobro = async () => {
    try {
      const { data } = await api.post(`/presupuestos/${id}/whatsapp-cobro`);
      setWaState({
        telefono: data.telefono || p.cliente_snapshot?.telefono || "",
        mensaje: data.mensaje || "",
        portal_url: data.portal_url || "",
        pay_url: data.pay_url || "",
      });
      setOpenWa(true);
      toast.success(`Link de cobro listo · ${data.cobro?.concepto || "Cobro"} ${data.cobro?.importe}€`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error preparando link de cobro");
    }
  };

  // === Cobros: habilitación manual ===
  const [openHabilitarCobro, setOpenHabilitarCobro] = useState(false);
  const [cobroDraft, setCobroDraft] = useState({ importe: "", concepto: "Anticipo 50%" });
  const [savingCobro, setSavingCobro] = useState(false);

  const totalPres = useMemo(() => Number(p?.totales?.total || 0), [p?.totales?.total]);
  const yaPagado = useMemo(() => Number(p?.importe_pagado || 0), [p?.importe_pagado]);
  const pendientePres = useMemo(() => Math.max(totalPres - yaPagado, 0), [totalPres, yaPagado]);

  const sugerirCobro = (tipo) => {
    if (tipo === "anticipo_50") {
      setCobroDraft({ importe: (totalPres * 0.5).toFixed(2), concepto: "Anticipo 50%" });
    } else if (tipo === "total") {
      setCobroDraft({ importe: totalPres.toFixed(2), concepto: "Pago total" });
    } else if (tipo === "resto") {
      setCobroDraft({ importe: pendientePres.toFixed(2), concepto: "Resto final" });
    }
  };

  const habilitarCobro = async () => {
    const importe = parseFloat(cobroDraft.importe);
    if (!importe || importe <= 0) { toast.error("Importe inválido"); return; }
    if (importe > pendientePres + 0.01) {
      toast.error(`El importe (${importe.toFixed(2)} €) supera el pendiente (${pendientePres.toFixed(2)} €)`);
      return;
    }
    setSavingCobro(true);
    try {
      await api.post(`/presupuestos/${id}/cobro/habilitar`, {
        importe,
        concepto: cobroDraft.concepto || "Cobro",
      });
      toast.success("Cobro habilitado en el portal del cliente");
      setOpenHabilitarCobro(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error habilitando cobro");
    } finally { setSavingCobro(false); }
  };

  const cancelarCobro = async () => {
    if (!window.confirm("¿Cancelar el cobro habilitado? El botón de pago desaparecerá del portal del cliente.")) return;
    try {
      await api.post(`/presupuestos/${id}/cobro/cancelar`);
      toast.success("Cobro cancelado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cancelando cobro");
    }
  };

  const enviarWhatsApp = async () => {
    try {
      const { data } = await api.post(`/presupuestos/${id}/whatsapp-link`, {
        telefono: waState.telefono,
        mensaje: waState.mensaje,
      });
      // Abrimos directamente WhatsApp Web (web.whatsapp.com) en lugar de wa.me,
      // porque wa.me redirige a api.whatsapp.com y muchos AdBlockers lo bloquean.
      window.open(data.url, "_blank", "noopener,noreferrer");
      await api.post(`/presupuestos/${id}/enviar`, { canal: "whatsapp", enviado_a: data.telefono });
      setOpenWa(false);
      load();
      toast.success("WhatsApp abierto · presupuesto marcado como enviado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando link de WhatsApp");
    }
  };

  // URLs directas para evitar redirección por api.whatsapp.com
  const waWebUrl = useMemo(() => {
    const tel = (waState.telefono || "").replace(/\D/g, "");
    const msg = encodeURIComponent(waState.mensaje || "");
    if (tel) return `https://web.whatsapp.com/send?phone=${tel}&text=${msg}`;
    return `https://web.whatsapp.com/send?text=${msg}`;
  }, [waState.telefono, waState.mensaje]);

  const waMobileUrl = useMemo(() => {
    const tel = (waState.telefono || "").replace(/\D/g, "");
    const msg = encodeURIComponent(waState.mensaje || "");
    if (tel) return `whatsapp://send?phone=${tel}&text=${msg}`;
    return `whatsapp://send?text=${msg}`;
  }, [waState.telefono, waState.mensaje]);

  const marcarComoEnviadoWa = async () => {
    try {
      await api.post(`/presupuestos/${id}/enviar`, { canal: "whatsapp", enviado_a: waState.telefono });
      setOpenWa(false);
      load();
      toast.success("Presupuesto marcado como enviado");
    } catch (_) { /* noop */ }
  };

  const copiarMensaje = async () => {
    try {
      await navigator.clipboard.writeText(waState.mensaje);
      toast.success("Mensaje copiado al portapapeles");
    } catch (e) {
      toast.error("No se pudo copiar (permisos del navegador)");
    }
  };

  const copiarSoloEnlace = async () => {
    try {
      await navigator.clipboard.writeText(waState.portal_url || "");
      toast.success("Enlace del portal copiado");
    } catch (e) {
      toast.error("No se pudo copiar el enlace");
    }
  };

  // ====== IA · Parsear presupuesto proveedor ======
  const iaAnalizar = async () => {
    if (iaModo === "pdf" && !iaFile) {
      toast.error("Arrastra un PDF o imagen del proveedor primero");
      return;
    }
    if (iaModo === "texto" && !iaTexto.trim()) {
      toast.error("Pega primero el texto del presupuesto del proveedor");
      return;
    }
    setIaLoading(true);
    setIaPreview(null);
    try {
      let data;
      if (iaModo === "pdf") {
        const fd = new FormData();
        fd.append("file", iaFile);
        const r = await api.post("/presupuestos/parse-provider-quote-pdf", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000,
        });
        data = r.data;
      } else {
        const r = await api.post("/presupuestos/parse-provider-quote", { texto: iaTexto }, { timeout: 60000 });
        data = r.data;
      }
      setIaPreview(data);
      if ((data.items || []).length === 0) {
        toast.warning("La IA no detectó ningún item. Revisa el archivo / texto.");
      } else {
        toast.success(`IA detectó ${data.items.length} concepto(s)`);
      }
    } catch (e) {
      toast.error("Error IA: " + (e?.response?.data?.detail || e.message));
    } finally {
      setIaLoading(false);
    }
  };

  const iaInsertar = async () => {
    if (!iaPreview?.items?.length) return;
    setIaLoading(true);
    try {
      if (iaModo === "pdf" && iaFile) {
        const fd = new FormData();
        fd.append("file", iaFile);
        fd.append("auto_add_to", id);
        await api.post("/presupuestos/parse-provider-quote-pdf", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000,
        });
      } else {
        await api.post("/presupuestos/parse-provider-quote", {
          texto: iaTexto,
          auto_add_to: id,
        });
      }
      toast.success(`${iaPreview.items.length} items añadidos al presupuesto`);
      setOpenIA(false);
      setIaTexto("");
      setIaFile(null);
      setIaPreview(null);
      await load();
    } catch (e) {
      toast.error("Error insertando items: " + (e?.response?.data?.detail || e.message));
    } finally {
      setIaLoading(false);
    }
  };

  const iaOnDrop = (ev) => {
    ev.preventDefault();
    setIaDragOver(false);
    const f = ev.dataTransfer?.files?.[0];
    if (!f) return;
    if (!/\.(pdf|png|jpe?g|webp)$/i.test(f.name)) {
      toast.error("Solo PDF o imagen (PNG/JPG/WEBP)");
      return;
    }
    setIaFile(f);
    setIaPreview(null);
  };

  const copiarTodo = async () => {
    const tel = waState.telefono ? `Teléfono: +${(waState.telefono || "").replace(/\D/g, "")}\n\n` : "";
    try {
      await navigator.clipboard.writeText(tel + (waState.mensaje || ""));
      toast.success("Teléfono y mensaje copiados");
    } catch (e) {
      toast.error("No se pudo copiar");
    }
  };

  const marcarEnviado = async (canal) => {
    await api.post(`/presupuestos/${id}/enviar`, { canal });
    load();
    toast.success(`Marcado como enviado (${canal})`);
  };

  const cambiarEstado = async (nuevo) => {
    await patchPres({ estado: nuevo });
    toast.success(`Estado: ${ESTADOS[nuevo]?.label || nuevo}`);
  };

  const totales = useMemo(() => calcTotales(p?.items, p?.iva_pct), [p?.items, p?.iva_pct]);

  if (loading || !p) {
    return <div className="p-6 text-zinc-400">Cargando…</div>;
  }

  const cfgEstado = ESTADOS[p.estado] || ESTADOS.borrador;

  return (
    <div className="p-6 max-w-[1800px] mx-auto" data-testid="presupuesto-detail">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Button size="sm" variant="ghost" onClick={() => nav("/presupuestos")} data-testid="btn-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <span className="font-mono text-amber-300">{p.numero}</span>
            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${cfgEstado.color}`}>{cfgEstado.label}</span>
            {p.lead_id && (
              <button type="button" onClick={() => nav(`/leads?lead=${p.lead_id}`)} data-testid="presupuesto-origen-lead"
                className="text-[11px] px-2 py-0.5 rounded border border-violet-500/40 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20">
                Origen: Lead {p.cliente_snapshot?.razon_social ? `· ${p.cliente_snapshot.razon_social}` : ""} ↗
              </button>
            )}
          </span>
        }
        subtitle={`${empresa.marca_comercial || "DNF"} · Total ${fmtEUR(totales.total)} (IVA incl.)`}
        action={
          <div className="flex items-center gap-2">
            <Select value={p.estado} onValueChange={cambiarEstado}>
              <SelectTrigger className="w-36 bg-zinc-900 border-zinc-800" data-testid="select-estado">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {Object.entries(ESTADOS).map(([k, c]) => (
                  <SelectItem key={k} value={k}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={generarPDF} disabled={genPdf}
              data-testid="btn-generar-pdf"
              className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold">
              {genPdf ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              {p.pdf_url ? "Regenerar PDF" : "Generar PDF"}
            </Button>
            {p.estado === "aceptado" && !p.peticion_id && (
              <Button onClick={convertirAPeticion} disabled={convertir}
                data-testid="btn-convertir-peticion"
                className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 font-semibold">
                {convertir ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Convertir a Petición
              </Button>
            )}
            {puedeAnularDerivar &&
              p.estado !== "aceptado" &&
              p.estado !== "anulado_por_cliente" && (
              <Button onClick={abrirAnularDerivar} disabled={anulando}
                data-testid="btn-anular-derivar"
                variant="outline"
                className="border-fuchsia-500/60 text-fuchsia-200 hover:bg-fuchsia-500/10">
                Anular y derivar
              </Button>
            )}
            {p.estado === "anulado_por_cliente" && p.express_ref && (
              <span data-testid="badge-express-ref"
                className="px-2.5 py-1 rounded text-[11px] font-mono font-bold bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-500/40">
                → Express {p.express_ref}
              </span>
            )}
            {p.peticion_id && (
              <Button variant="outline" onClick={() => nav(`/peticiones/${p.peticion_id}`)}
                data-testid="btn-ver-peticion"
                className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10">
                Ver petición →
              </Button>
            )}
          </div>
        }
      />

      {/* Acciones envío */}
      {p.pdf_url && (
        <div className="bg-emerald-900/20 border border-emerald-500/40 rounded-xl p-4 mb-5 flex items-center gap-3 flex-wrap">
          <FileText className="w-5 h-5 text-emerald-300" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-emerald-100 font-semibold">PDF disponible</div>
            <div className="text-xs text-zinc-400">Generado {p.pdf_generated_at ? new Date(p.pdf_generated_at).toLocaleString("es-ES") : ""}</div>
          </div>
          <Button size="sm" variant="outline" onClick={abrirPDF}
            data-testid="btn-descargar-pdf"
            className="border-zinc-700 hover:bg-zinc-800">
            <Download className="w-4 h-4 mr-2" /> Descargar / Ver
          </Button>
          <Button size="sm" onClick={abrirDialogoWhatsApp} data-testid="btn-enviar-whatsapp"
            className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 font-semibold">
            <MessageCircle className="w-4 h-4 mr-2" /> Enviar por WhatsApp
          </Button>
          <Button size="sm" variant="ghost" onClick={() => marcarEnviado("email")}
            data-testid="btn-marcar-email">
            <Send className="w-4 h-4 mr-2" /> Marcar enviado (email)
          </Button>
        </div>
      )}

      {/* Panel COBROS — sólo cuando está aceptado y aún hay pendiente */}
      {p.estado === "aceptado" && pendientePres > 0.01 && (
        <div className="bg-gradient-to-r from-amber-900/30 to-amber-800/10 border border-amber-500/40 rounded-xl p-4 mb-5"
             data-testid="panel-cobros">
          {p.cobro_habilitado ? (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-2xl">💰</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-amber-100 font-semibold flex items-center gap-2">
                  Cobro habilitado en el portal del cliente
                  <span className="text-[10px] uppercase tracking-wider bg-amber-500/30 text-amber-100 px-2 py-0.5 rounded font-bold">ACTIVO</span>
                </div>
                <div className="text-xs text-zinc-300 mt-0.5">
                  <b className="text-amber-200">{p.cobro_habilitado.concepto}</b> · <b className="text-amber-200">{Number(p.cobro_habilitado.importe).toFixed(2)} €</b>
                  <span className="text-zinc-500"> · habilitado {new Date(p.cobro_habilitado.habilitado_at).toLocaleString("es-ES")}</span>
                </div>
              </div>
              <Button size="sm" onClick={abrirDialogoCobro} data-testid="btn-enviar-link-cobro"
                className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-bold">
                <MessageCircle className="w-4 h-4 mr-2" /> Enviar por WhatsApp
              </Button>
              <Button size="sm" variant="ghost" onClick={cancelarCobro}
                data-testid="btn-cancelar-cobro"
                className="text-zinc-400 hover:text-red-400">
                Cancelar
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-2xl">📩</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-amber-100 font-semibold">Cliente aceptó · sin cobro habilitado</div>
                <div className="text-xs text-zinc-400">
                  Pendiente: <b className="text-amber-200">{pendientePres.toFixed(2)} €</b>
                  {yaPagado > 0.01 && <span className="text-zinc-500"> · ya pagado: {yaPagado.toFixed(2)} €</span>}
                  <br />
                  El cliente verá un mensaje "te avisaremos cuando esté listo el cobro" hasta que habilites uno.
                </div>
              </div>
              <Button size="sm" onClick={() => { sugerirCobro(yaPagado < 0.01 ? "anticipo_50" : "resto"); setOpenHabilitarCobro(true); }}
                data-testid="btn-habilitar-cobro"
                className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-bold">
                <Plus className="w-4 h-4 mr-2" /> Habilitar cobro
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Listado de pagos confirmados + botón de devolución VISA (Redsys refund) */}
      {p.id && <PagosPresupuestoPanel presupuestoId={p.id} onChange={load} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        {/* Cliente */}
        <div className="lg:col-span-2 bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <div className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold mb-2 flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" /> Cliente
          </div>
          <div className="flex items-center gap-2 mb-3">
            <Popover open={openCliCombo} onOpenChange={setOpenCliCombo}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={openCliCombo}
                  className="bg-zinc-900 border-zinc-800 hover:bg-zinc-900 hover:text-zinc-100 flex-1 justify-between font-normal text-zinc-100"
                  data-testid="select-cliente"
                >
                  <span className="truncate">
                    {p.cliente_id
                      ? (clientes.find((c) => c.id === p.cliente_id)?.nombre || p.cliente_snapshot?.razon_social || "Cliente")
                      : "Sin cliente (manual)"}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>              <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] bg-zinc-950 border-zinc-800" align="start">
                <Command className="bg-zinc-950 text-zinc-100">
                  <CommandInput placeholder="Buscar cliente por nombre, CIF, ciudad..."
                    data-testid="input-buscar-cliente"
                    className="text-zinc-100 placeholder:text-zinc-500" />
                  <CommandEmpty className="py-4 text-center text-sm text-zinc-500">Sin resultados</CommandEmpty>
                  <CommandList className="max-h-72">
                    <CommandGroup>
                      <CommandItem
                        value="__sin_cliente__"
                        onSelect={() => { cambiarCliente(null); setOpenCliCombo(false); }}
                        className="text-zinc-300 aria-selected:bg-zinc-800"
                        data-testid="cliente-opt-sin"
                      >
                        <Check className={`mr-2 h-4 w-4 ${!p.cliente_id ? "opacity-100" : "opacity-0"}`} />
                        <span className="italic text-zinc-400">Sin cliente (manual)</span>
                      </CommandItem>
                      {clientes.map((c) => {
                        const searchKey = `${c.nombre || ""} ${c.cif || ""} ${c.direccion_fiscal || ""} ${c.email_contacto || ""}`.toLowerCase();
                        return (
                          <CommandItem
                            key={c.id}
                            value={searchKey}
                            onSelect={() => { cambiarCliente(c.id); setOpenCliCombo(false); }}
                            className="text-zinc-100 aria-selected:bg-amber-500/15 aria-selected:text-amber-100"
                            data-testid={`cliente-opt-${c.id}`}
                          >
                            <Check className={`mr-2 h-4 w-4 ${p.cliente_id === c.id ? "opacity-100 text-amber-400" : "opacity-0"}`} />
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-medium">{c.nombre}</div>
                              {(c.cif || c.direccion_fiscal) && (
                                <div className="truncate text-[11px] text-zinc-500">
                                  {c.cif ? `${c.cif} · ` : ""}{c.direccion_fiscal || ""}
                                </div>
                              )}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setOpenNuevoCli(true)}
              data-testid="btn-nuevo-cliente-pres"
              className="bg-zinc-900 border-zinc-800 hover:bg-amber-500/10 hover:text-amber-200 hover:border-amber-500/50 shrink-0"
              title="Crear cliente nuevo y asignarlo"
            >
              <UserPlus className="w-4 h-4 mr-1.5" />
              Nuevo
            </Button>
          </div>
          {p.cliente_snapshot && (
            <>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                  Datos en este presupuesto
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={abrirEditarSnapshot}
                  data-testid="btn-editar-snapshot"
                  className="h-6 px-2 text-[11px] text-amber-300 hover:text-amber-200 hover:bg-amber-500/10"
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  Editar
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div><span className="text-zinc-500">Razón social:</span> <span className="text-zinc-100">{p.cliente_snapshot.razon_social || "—"}</span></div>
                <div><span className="text-zinc-500">CIF:</span> <span className="text-zinc-100">{p.cliente_snapshot.cif || "—"}</span></div>
                <div className="md:col-span-2"><span className="text-zinc-500">Dirección:</span> <span className="text-zinc-100">{p.cliente_snapshot.direccion || "—"}</span></div>
                <div><span className="text-zinc-500">Tel:</span> <span className="text-zinc-100">{p.cliente_snapshot.telefono || "—"}</span></div>
                <div><span className="text-zinc-500">Email:</span> <span className="text-zinc-100">{p.cliente_snapshot.email || "—"}</span></div>
                <div className="md:col-span-2"><span className="text-zinc-500">Atn.:</span> <span className="text-zinc-100">{p.cliente_snapshot.contacto_nombre || "—"}</span></div>
                {p.cliente_snapshot.observaciones && (
                  <div className="md:col-span-2 bg-amber-500/5 border border-amber-500/25 rounded px-2 py-1.5">
                    <span className="text-amber-300 text-[10px] uppercase tracking-wider">Observaciones / Ref.:</span>
                    <div className="text-zinc-100 whitespace-pre-wrap text-[13px] mt-0.5">{p.cliente_snapshot.observaciones}</div>
                  </div>
                )}
              </div>
            </>
          )}
          {!p.cliente_snapshot && (
            <div className="flex items-center justify-between bg-zinc-900/60 border border-dashed border-zinc-700 rounded-md px-3 py-2 text-xs text-zinc-400">
              <span>Sin datos de cliente. Crea uno o introduce datos manualmente.</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={abrirEditarSnapshot}
                data-testid="btn-introducir-datos-manuales"
                className="h-6 px-2 text-[11px] text-amber-300 hover:text-amber-200"
              >
                <Pencil className="w-3 h-3 mr-1" />
                Introducir datos
              </Button>
            </div>
          )}
        </div>

        {/* Datos presupuesto */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <div className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold mb-2">Datos</div>
          <div className="space-y-2.5">
            <div>
              <Label className="text-[11px] text-zinc-400 flex items-center gap-1">
                Referencia
                <span className="text-[10px] text-zinc-600 normal-case font-normal">
                  · campaña, pedido cliente, asunto…
                </span>
              </Label>
              <Input
                value={p.referencia || ""}
                onChange={(e) => setP({ ...p, referencia: e.target.value })}
                onBlur={() => patchPres({ referencia: (p.referencia || "").trim() || null })}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                placeholder={(() => {
                  // Sugerencia automática para guiar al usuario
                  const cli = p.cliente_snapshot?.razon_social || "";
                  const fecha = p.fecha ? new Date(p.fecha).toLocaleDateString("es-ES", { month: "short", year: "numeric" }) : "";
                  if (cli && fecha) return `Ej: Campaña ${fecha} · ${cli}`;
                  if (cli) return `Ej: Pedido ${cli}`;
                  return "Ej: Campaña Black Friday 2026";
                })()}
                data-testid="input-referencia"
                className="bg-zinc-900 border-zinc-800 h-8"
              />
            </div>
            <div>
              <Label className="text-[11px] text-zinc-400">Fecha</Label>
              <Input type="date" value={p.fecha || ""}
                onChange={(e) => patchPres({ fecha: e.target.value })}
                data-testid="input-fecha"
                className="bg-zinc-900 border-zinc-800 h-8" />
            </div>
            <div>
              <Label className="text-[11px] text-zinc-400">Validez hasta</Label>
              <Input type="date" value={p.fecha_validez || ""}
                onChange={(e) => patchPres({ fecha_validez: e.target.value })}
                data-testid="input-validez"
                className="bg-zinc-900 border-zinc-800 h-8" />
            </div>
            <div>
              <Label className="text-[11px] text-zinc-400">IVA %</Label>
              <Input type="number" step="0.1" value={p.iva_pct}
                onChange={(e) => patchPres({ iva_pct: parseFloat(e.target.value) || 0 })}
                onFocus={(e) => e.target.select()}
                data-testid="input-iva"
                className="bg-zinc-900 border-zinc-800 h-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Aprobación parcial por partidas (Sep 2026) */}
      <PartidasAprobacionPanel p={p} onChanged={load} canEdit={hasRole(["admin", "comercial"])} />

      {/* Items */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold flex items-center gap-1.5">
            <Calculator className="w-3.5 h-3.5" /> Conceptos del presupuesto ({p.items?.length || 0})
          </div>
          <div className="flex items-center gap-2">
            <LearnerHint label="Pega el texto del presupuesto del proveedor y la IA extrae líneas">
              <Button size="sm" onClick={() => setOpenIA(true)}
                data-testid="btn-pegar-ia"
                variant="outline"
                className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10 h-8">
                <span className="mr-1.5">✨</span> Pegar presupuesto proveedor (IA)
              </Button>
            </LearnerHint>
            <Button size="sm" onClick={() => setOpenAddItem(true)}
              data-testid="btn-add-item"
              className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 h-8">
              <Plus className="w-3.5 h-3.5 mr-1" /> Añadir concepto
            </Button>
          </div>
        </div>

        {(p.items || []).length === 0 && (
          <div className="text-center text-zinc-500 py-8 text-sm italic">
            Sin conceptos aún. Pulsa "Añadir concepto" para arrancar.
          </div>
        )}

        <div className="space-y-2">
          {(p.items || []).map((it) => (
            <ItemRow key={it.id}
              item={it}
              onChange={(updates) => updateItem(it.id, updates)}
              onDelete={() => deleteItem(it.id)}
              onDuplicate={() => duplicarItem(it.id)}
              onOpenCalc={() => abrirCalc(it.id)}
              onUploadImg={(file) => subirImagenItem(it.id, file)}
              onOpenProveedor={() => setOpenProveedor(it.id)}
              fileRefImg={fileRefImg}
            />
          ))}
        </div>

        {/* Totales */}
        <div className="flex justify-end mt-4 pt-4 border-t border-zinc-800">
          <div className="w-72 space-y-1.5 text-sm">
            <div className="flex justify-between text-zinc-300">
              <span>Subtotal</span><span className="font-semibold">{fmtEUR(totales.subtotal)}</span>
            </div>
            <div className="flex justify-between text-zinc-400 text-xs">
              <span>IVA ({p.iva_pct}%)</span><span>{fmtEUR(totales.iva)}</span>
            </div>
            <div className="flex justify-between border-t border-amber-500/40 pt-2 text-lg">
              <span className="text-zinc-100 font-bold">TOTAL</span>
              <span className="text-amber-300 font-bold">{fmtEUR(totales.total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Condiciones y notas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <Label className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold">Condiciones de pago (sale en PDF)</Label>
          <Textarea value={p.condiciones_pago || ""}
            onChange={(e) => setP({ ...p, condiciones_pago: e.target.value })}
            onBlur={() => patchPres({ condiciones_pago: p.condiciones_pago })}
            data-testid="textarea-condiciones"
            placeholder="50% al confirmar el pedido, 50% antes de la entrega…"
            className="bg-zinc-900 border-zinc-800 mt-2 min-h-[90px]" />
        </div>
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <Label className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold">Notas para el cliente (sale en PDF)</Label>
          <Textarea value={p.notas_cliente || ""}
            onChange={(e) => setP({ ...p, notas_cliente: e.target.value })}
            onBlur={() => patchPres({ notas_cliente: p.notas_cliente })}
            data-testid="textarea-notas-cliente"
            placeholder="Plazos especiales, observaciones del trabajo…"
            className="bg-zinc-900 border-zinc-800 mt-2 min-h-[90px]" />
        </div>
      </div>

      {/* Adjuntos internos */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold flex items-center gap-1.5">
            <Paperclip className="w-3.5 h-3.5" /> Adjuntos internos (NO salen en el PDF)
            <span className="text-zinc-500 normal-case tracking-normal">— presupuestos de proveedor, croquis, notas privadas</span>
          </div>
          <Button size="sm" onClick={() => fileRefAdj.current?.click()}
            data-testid="btn-subir-adjunto-interno"
            className="bg-zinc-800 hover:bg-zinc-700 h-8">
            <Plus className="w-3.5 h-3.5 mr-1" /> Subir adjunto
          </Button>
          <input ref={fileRefAdj} type="file" className="hidden"
            onChange={(e) => { subirAdjuntoInterno(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        {(p.adjuntos_internos || []).length === 0 && (
          <div className="text-xs text-zinc-500 italic">Sin adjuntos internos.</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {(p.adjuntos_internos || []).map((a) => (
            <div key={a.id} data-testid={`adj-interno-${a.id}`}
              className="flex items-center gap-2 bg-zinc-950/50 border border-zinc-800 rounded p-2 text-sm">
              <FileText className="w-4 h-4 text-zinc-400 flex-shrink-0" />
              <a href={a.url} target="_blank" rel="noreferrer"
                className="flex-1 truncate text-zinc-200 hover:text-amber-300" title={a.nombre}>{a.nombre}</a>
              <Button size="sm" variant="ghost" onClick={() => deleteAdjuntoInterno(a.id)}
                data-testid={`btn-del-adj-${a.id}`}
                className="h-6 w-6 p-0 text-zinc-500 hover:text-red-400">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* PORTAL CLIENTE — QR + acciones */}
      <div className="bg-gradient-to-br from-emerald-900/20 to-emerald-900/5 border border-emerald-500/40 rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-[11px] uppercase tracking-wider text-emerald-300 font-semibold flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5" /> Portal del cliente (acceso público sin login)
          </div>
          {!p.portal_token && (
            <Button size="sm" onClick={activarPortal} data-testid="btn-activar-portal"
              className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 font-semibold h-8">
              <Link2 className="w-3.5 h-3.5 mr-1" /> Activar portal
            </Button>
          )}
        </div>

        {!p.portal_token && (
          <div className="text-xs text-zinc-400 italic">
            Activa el portal para que el cliente pueda ver el presupuesto, aceptar/rechazar y subir archivos desde una URL pública. La URL se incluye automáticamente con QR en el PDF.
          </div>
        )}

        {p.portal_token && (
          <>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <code className="flex-1 min-w-0 text-xs bg-zinc-950/60 border border-zinc-800 rounded px-2 py-1.5 text-emerald-300 break-all font-mono"
                data-testid="portal-url">{portalUrl}</code>
              <Button size="sm" variant="outline" onClick={copiarUrlPortal} data-testid="btn-copiar-url-portal"
                className="border-zinc-700 hover:bg-zinc-800 h-8">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copiar URL
              </Button>
              <a href={portalUrl} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" data-testid="btn-abrir-portal"
                  className="border-zinc-700 hover:bg-zinc-800 h-8">
                  <Eye className="w-3.5 h-3.5 mr-1" /> Abrir
                </Button>
              </a>
            </div>

            {/* Estado del cliente */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
              <div className="bg-zinc-950/40 border border-zinc-800 rounded p-2.5 text-sm">
                <div className="text-[10px] uppercase text-zinc-500 mb-0.5">Visitas</div>
                <div className="text-zinc-100 font-semibold" data-testid="portal-visitas">{p.portal_visitas || 0}</div>
                {p.portal_ultima_visita_at && <div className="text-[10px] text-zinc-500" data-testid="portal-ultima-visita">Última: {new Date(p.portal_ultima_visita_at).toLocaleString("es-ES")}</div>}
                <HistorialVistasPresupuesto presupuesto={p} />
              </div>
              <div className="bg-zinc-950/40 border border-zinc-800 rounded p-2.5 text-sm">
                <div className="text-[10px] uppercase text-zinc-500 mb-0.5">Visto por primera vez</div>
                <div className="text-zinc-100 font-semibold">{p.portal_visto_at ? new Date(p.portal_visto_at).toLocaleString("es-ES") : "—"}</div>
              </div>
              <div className="bg-zinc-950/40 border border-zinc-800 rounded p-2.5 text-sm">
                <div className="text-[10px] uppercase text-zinc-500 mb-0.5">Decisión cliente</div>
                <div className="text-zinc-100 font-semibold capitalize">
                  {p.cliente_decision ? p.cliente_decision.replace("_", " ") : "— pendiente —"}
                </div>
              </div>
            </div>

            {p.cliente_comentario && (
              <div className="bg-amber-900/20 border border-amber-500/30 rounded p-3 mb-3 text-sm">
                <div className="text-[10px] uppercase text-amber-300 mb-1">Comentario del cliente</div>
                <div className="text-amber-100 italic">"{p.cliente_comentario}"</div>
              </div>
            )}

            {/* Archivos para el cliente (subidos por DNF) */}
            <div className="border-t border-emerald-500/20 pt-3 mt-2">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider text-emerald-200 font-semibold">
                  Archivos visibles al cliente (renders, facturas, planos…)
                </div>
                <Button size="sm" onClick={() => fileRefArchCli.current?.click()}
                  data-testid="btn-subir-arch-cliente"
                  className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/40 h-7">
                  <Upload className="w-3 h-3 mr-1" /> Subir para cliente
                </Button>
                <input ref={fileRefArchCli} type="file" className="hidden"
                  onChange={(e) => { subirArchivoParaCliente(e.target.files?.[0]); e.target.value = ""; }} />
              </div>
              {(p.archivos_para_cliente || []).length === 0 && (
                <div className="text-xs text-zinc-500 italic">Aún no has subido nada para el cliente.</div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {(p.archivos_para_cliente || []).map((a) => (
                  <div key={a.id} className="flex items-center gap-1.5 bg-zinc-950/60 border border-zinc-800 rounded p-2 text-xs"
                    data-testid={`arch-dnf-${a.id}`}>
                    <FileText className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    <a href={a.url} target="_blank" rel="noreferrer"
                      className="flex-1 truncate text-zinc-200 hover:text-emerald-300" title={a.nombre}>{a.nombre}</a>
                    <Button size="sm" variant="ghost" onClick={() => borrarArchivoParaCliente(a.id)}
                      className="h-5 w-5 p-0 text-zinc-500 hover:text-red-400">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Archivos enviados por el cliente */}
            {(p.archivos_cliente || []).length > 0 && (
              <div className="border-t border-emerald-500/20 pt-3 mt-3">
                <div className="text-[11px] uppercase tracking-wider text-blue-200 font-semibold mb-2 flex items-center gap-1.5">
                  📩 Archivos enviados por el cliente
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {(p.archivos_cliente || []).map((a) => (
                    <div key={a.id} className="flex items-center gap-1.5 bg-blue-950/30 border border-blue-500/30 rounded p-2 text-xs"
                      data-testid={`arch-cli-${a.id}`}>
                      <Paperclip className="w-3.5 h-3.5 text-blue-300 flex-shrink-0" />
                      <a href={a.url} target="_blank" rel="noreferrer"
                        className="flex-1 truncate text-blue-100 hover:text-blue-200" title={a.nombre}>{a.nombre}</a>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* CHAT cliente↔DNF */}
            <div className="border-t border-emerald-500/20 pt-3 mt-3">
              <div className="text-[11px] uppercase tracking-wider text-emerald-200 font-semibold mb-2 flex items-center gap-1.5">
                💬 Chat con el cliente
                {(p.mensajes || []).length > 0 && (
                  <span className="text-zinc-400 normal-case">({(p.mensajes || []).length} mensajes)</span>
                )}
              </div>
              <div className="bg-zinc-950/60 border border-zinc-800 rounded p-3 max-h-72 overflow-y-auto mb-2 space-y-2"
                data-testid="chat-presupuesto">
                {(p.mensajes || []).length === 0 && (
                  <div className="text-xs text-zinc-500 italic text-center py-4">
                    Aún no hay mensajes. Escribe el primero abajo para iniciar la conversación con el cliente.
                  </div>
                )}
                {(p.mensajes || []).map((m) => {
                  const isCliente = m.autor_tipo === "cliente";
                  return (
                    <div key={m.id} className={`flex ${isCliente ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[78%] rounded-lg px-3 py-2 text-sm ${
                        isCliente
                          ? "bg-blue-900/40 border border-blue-500/30 text-blue-50"
                          : "bg-emerald-900/40 border border-emerald-500/30 text-emerald-50"
                      }`}>
                        <div className="text-[10px] opacity-70 mb-0.5">
                          <b>{m.autor_nombre}</b> · {new Date(m.created_at).toLocaleString("es-ES")}
                        </div>
                        <div className="whitespace-pre-wrap">{m.texto}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Input value={chatMsg} onChange={(e) => setChatMsg(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviarChat(); } }}
                  data-testid="chat-input"
                  placeholder="Escribe un mensaje al cliente…"
                  className="bg-zinc-900 border-zinc-800 flex-1" />
                <Button size="sm" onClick={enviarChat} disabled={sendingChat || !chatMsg.trim()}
                  data-testid="chat-send"
                  className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 font-semibold">
                  <Send className="w-3.5 h-3.5 mr-1" /> Enviar
                </Button>
              </div>
              <div className="text-[10px] text-zinc-500 mt-1.5 italic">
                El cliente verá este chat en su portal (URL pública). Te avisamos cuando responda.
              </div>
            </div>
          </>
        )}
      </div>

      {/* Dialog añadir item desde catálogo */}
      <Dialog open={openAddItem} onOpenChange={setOpenAddItem}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl" data-testid="dialog-add-item">
          <DialogHeader>
            <DialogTitle className="text-amber-300">Añadir concepto</DialogTitle>
          </DialogHeader>
          <CatalogoPicker
            catalogo={catalogo}
            onPick={addItemDesdeCatalogo}
            onLibre={addItemLibre}
            onExterno={addItemExterno}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog proveedor externo */}
      <ProveedorDialog
        item={(p.items || []).find((it) => it.id === openProveedor)}
        onClose={() => setOpenProveedor(null)}
        onSave={(updates) => { updateItem(openProveedor, updates); setOpenProveedor(null); }}
        onUploadAdjunto={(file) => subirAdjuntoProveedor(openProveedor, file)}
      />

      {/* Dialog Calculadora gráfica */}
      <CalcGraficaDialog
        item={(p.items || []).find((it) => it.id === openCalc)}
        materiales={materiales || []}
        onClose={() => setOpenCalc(null)}
        onApply={(updates) => { updateItem(openCalc, updates); setOpenCalc(null); }}
      />

      {/* Dialog WhatsApp */}
      <Dialog open={openWa} onOpenChange={setOpenWa}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-xl" data-testid="dialog-whatsapp">
          <DialogHeader>
            <DialogTitle className="text-emerald-300 flex items-center gap-2">
              <MessageCircle className="w-5 h-5" /> Enviar por WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-zinc-400">Teléfono del cliente (con prefijo país)</Label>
              <Input
                value={waState.telefono}
                onChange={(e) => setWaState({ ...waState, telefono: e.target.value })}
                data-testid="input-wa-telefono"
                placeholder="34666123456 (sin + ni espacios)"
                className="bg-zinc-900 border-zinc-800" />
              <div className="text-[11px] text-zinc-500 mt-1">
                Si lo dejas vacío, WhatsApp Web te pedirá introducir el número antes de enviar.
                Para España, el prefijo es <b>34</b> seguido del móvil (ej: <b>34666123456</b>).
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-zinc-400">Mensaje (editable)</Label>
                <Button size="sm" variant="ghost" onClick={copiarMensaje}
                  data-testid="btn-copiar-mensaje"
                  className="h-6 text-xs text-zinc-400 hover:text-emerald-300">
                  <Copy className="w-3 h-3 mr-1" /> Copiar
                </Button>
              </div>
              <Textarea
                value={waState.mensaje}
                onChange={(e) => setWaState({ ...waState, mensaje: e.target.value })}
                data-testid="textarea-wa-mensaje"
                className="bg-zinc-900 border-zinc-800 min-h-[160px] font-mono text-sm" />
            </div>
            <div className="bg-emerald-900/20 border border-emerald-500/30 rounded p-3 text-xs text-emerald-100">
              <b>Elige el método que mejor te funcione:</b><br />
              • <b>WhatsApp Web</b>: abre directamente <code>web.whatsapp.com</code> con el chat (no pasa por <code>api.whatsapp.com</code>, esquiva AdBlockers).<br />
              • <b>WhatsApp Móvil</b>: deeplink que abre la app del móvil si estás en el móvil.<br />
              • <b>Copiar</b>: si todo falla, copias el mensaje y lo pegas en WhatsApp manualmente.<br /><br />
              <b>No usa la API de Meta</b> — funciona ya, sin verificación.
            </div>
            {waState.pay_url && (
              <div className="bg-amber-900/20 border border-amber-500/40 rounded p-3 text-xs text-amber-100" data-testid="pay-url-block">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <b className="flex items-center gap-1.5">💳 Link de pago directo (1 toque)</b>
                  <Button size="sm" variant="ghost"
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(waState.pay_url); toast.success("Link de pago copiado"); }
                      catch { toast.error("No se pudo copiar"); }
                    }}
                    data-testid="btn-copiar-pay-url"
                    className="h-6 text-xs text-amber-300 hover:text-amber-100 hover:bg-amber-500/10">
                    <Copy className="w-3 h-3 mr-1" /> Copiar
                  </Button>
                </div>
                <div className="font-mono text-[11px] break-all bg-zinc-950/60 border border-amber-500/20 rounded px-2 py-1.5">
                  {waState.pay_url}
                </div>
                <div className="text-[11px] text-amber-200/80 mt-1.5">
                  Cuando el cliente toque este link va <b>directo al TPV de La Caixa</b>, sin pasar por el portal completo. Cero fricción para clientes "non-tech".
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="ghost" onClick={() => setOpenWa(false)} data-testid="btn-cancel-wa">
              Cancelar
            </Button>
            {waState.portal_url && (
              <Button onClick={copiarSoloEnlace} data-testid="btn-copiar-enlace"
                variant="outline"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
                <Link2 className="w-4 h-4 mr-2" /> Copiar solo enlace
              </Button>
            )}
            <Button onClick={copiarTodo} data-testid="btn-copiar-todo"
              variant="outline"
              className="border-zinc-700 hover:bg-zinc-800">
              <Copy className="w-4 h-4 mr-2" /> Copiar todo
            </Button>
            <a href={waMobileUrl} target="_blank" rel="noopener noreferrer"
              onClick={marcarComoEnviadoWa} data-testid="btn-wa-movil">
              <Button variant="outline" className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Móvil
              </Button>
            </a>
            <a href={waWebUrl} target="_blank" rel="noopener noreferrer"
              onClick={marcarComoEnviadoWa} data-testid="btn-wa-web">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 font-semibold">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Web
              </Button>
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog IA: pegar presupuesto proveedor */}
      <Dialog open={openIA} onOpenChange={(v) => {
        setOpenIA(v);
        if (!v) { setIaTexto(""); setIaFile(null); setIaPreview(null); }
      }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-ia">
          <DialogHeader>
            <DialogTitle className="text-violet-300 flex items-center gap-2">
              <span>✨</span> Pegar presupuesto de proveedor (IA)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-violet-900/15 border border-violet-500/30 rounded p-3 text-xs text-violet-100">
              <b>Cómo funciona</b>: sube el PDF/imagen del proveedor o pega su texto.
              Gemini detecta automáticamente conceptos, dimensiones, cantidades y precios y los
              inserta como líneas listas para que les pongas tu margen.
            </div>

            {/* Toggle modo */}
            <div className="flex items-center gap-1 p-1 bg-zinc-900 rounded-lg border border-zinc-800 w-fit">
              <button
                type="button"
                onClick={() => { setIaModo("pdf"); setIaPreview(null); }}
                data-testid="ia-modo-pdf"
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  iaModo === "pdf"
                    ? "bg-violet-500 text-white"
                    : "text-zinc-400 hover:text-zinc-100"
                }`}>
                📎 Subir PDF / Imagen
              </button>
              <button
                type="button"
                onClick={() => { setIaModo("texto"); setIaPreview(null); }}
                data-testid="ia-modo-texto"
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  iaModo === "texto"
                    ? "bg-violet-500 text-white"
                    : "text-zinc-400 hover:text-zinc-100"
                }`}>
                ✏️ Pegar texto
              </button>
            </div>

            {iaModo === "pdf" ? (
              <div>
                <Label className="text-xs text-zinc-400">Archivo del proveedor (PDF, PNG, JPG, WEBP — max 25 MB)</Label>
                <div
                  onDragOver={(e) => { e.preventDefault(); setIaDragOver(true); }}
                  onDragLeave={() => setIaDragOver(false)}
                  onDrop={iaOnDrop}
                  onClick={() => iaFileInputRef.current?.click()}
                  data-testid="ia-dropzone"
                  className={`mt-1 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    iaDragOver
                      ? "border-violet-400 bg-violet-500/10"
                      : iaFile
                        ? "border-emerald-500/50 bg-emerald-500/5"
                        : "border-zinc-700 hover:border-zinc-600 bg-zinc-900/40"
                  }`}>
                  <input
                    ref={iaFileInputRef}
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) { setIaFile(f); setIaPreview(null); }
                    }}
                    className="hidden"
                    data-testid="ia-file-input"
                  />
                  {iaFile ? (
                    <div className="space-y-1">
                      <div className="text-emerald-300 font-semibold flex items-center justify-center gap-2">
                        <FileText className="w-5 h-5" />
                        {iaFile.name}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {(iaFile.size / 1024).toFixed(1)} KB · {iaFile.type || "archivo"}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setIaFile(null); setIaPreview(null); }}
                        className="text-xs text-zinc-400 hover:text-red-300 mt-2"
                        data-testid="ia-file-remove"
                      >
                        Quitar archivo
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                      <div className="text-sm text-zinc-300 font-medium">
                        Arrastra el PDF del proveedor aquí
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        o haz clic para seleccionarlo
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <Label className="text-xs text-zinc-400">Texto del presupuesto del proveedor</Label>
                <Textarea
                  value={iaTexto}
                  onChange={(e) => { setIaTexto(e.target.value); setIaPreview(null); }}
                  data-testid="textarea-ia-texto"
                  placeholder="Pega aquí el texto completo del presupuesto del proveedor…&#10;&#10;Ejemplo:&#10;1. Lona 300x150 cm impresa - 1 ud x 12€/m²&#10;2. Vinilo polimérico 200x80 - 2 uds x 19,50€/m²&#10;3. Montaje 4h x 28€"
                  className="bg-zinc-900 border-zinc-800 min-h-[180px] font-mono text-xs"
                  disabled={iaLoading}
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  {iaTexto.length} caracteres · La IA procesará hasta 30.000 caracteres.
                </div>
              </div>
            )}

            {iaPreview && (
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-3 space-y-2">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-zinc-400">Detectado:</span>
                  {iaPreview.proveedor_detectado && (
                    <span className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-200">
                      🏭 {iaPreview.proveedor_detectado}
                    </span>
                  )}
                  {iaPreview.numero_presupuesto_detectado && (
                    <span className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-200">
                      #{iaPreview.numero_presupuesto_detectado}
                    </span>
                  )}
                  {iaPreview.totales_detectados?.total && (
                    <span className="px-2 py-0.5 bg-emerald-900/40 text-emerald-300 rounded">
                      Total: {fmtEUR(iaPreview.totales_detectados.total)}
                    </span>
                  )}
                </div>
                {(iaPreview.warnings || []).length > 0 && (
                  <div className="text-[11px] text-amber-300 space-y-0.5">
                    {iaPreview.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                  </div>
                )}
                <div className="space-y-1">
                  {(iaPreview.items || []).map((it, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs bg-zinc-950 border border-zinc-800 rounded p-2"
                      data-testid={`ia-item-${i}`}>
                      <span className="text-violet-300 font-mono shrink-0">#{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-zinc-100 truncate">{it.descripcion}</div>
                        <div className="text-[10px] text-zinc-500 mt-0.5">
                          {it.cantidad} {it.unidad}
                          {it.base_cm && it.altura_cm ? ` · ${it.base_cm}×${it.altura_cm} cm` : ""}
                          {" · "}
                          <span className="text-amber-300">{fmtEUR(it.precio_unitario)}/{it.unidad}</span>
                          {" · "}
                          <span className="text-zinc-400">{it.categoria}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(iaPreview.items || []).length === 0 && (
                    <div className="text-center text-zinc-500 italic py-2 text-xs">
                      La IA no extrajo ningún item del texto.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="ghost" onClick={() => setOpenIA(false)} data-testid="btn-ia-cancel">
              Cancelar
            </Button>
            {!iaPreview ? (
              <Button onClick={iaAnalizar}
                disabled={iaLoading || (iaModo === "pdf" ? !iaFile : !iaTexto.trim())}
                data-testid="btn-ia-analizar"
                className="bg-violet-500 hover:bg-violet-600 text-white">
                {iaLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <span className="mr-2">✨</span>}
                {iaLoading ? "Analizando…" : "Analizar con IA"}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setIaPreview(null)}
                  data-testid="btn-ia-reanalizar"
                  className="border-zinc-700 hover:bg-zinc-800">
                  Editar texto y reanalizar
                </Button>
                <Button onClick={iaInsertar} disabled={iaLoading || (iaPreview.items || []).length === 0}
                  data-testid="btn-ia-insertar"
                  className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 font-semibold">
                  {iaLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Añadir {(iaPreview.items || []).length} concepto(s)
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {saving && (
        <div className="fixed bottom-4 right-4 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-zinc-400 flex items-center gap-2 z-50">
          <Save className="w-3 h-3 animate-pulse" /> Guardando…
        </div>
      )}

      {/* === Dialog: Nuevo cliente rápido === */}
      <Dialog open={openNuevoCli} onOpenChange={setOpenNuevoCli}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg" data-testid="dialog-nuevo-cliente-pres">
          <DialogHeader>
            <DialogTitle className="text-amber-300 flex items-center gap-2">
              <UserPlus className="w-4 h-4" /> Nuevo cliente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs text-zinc-400">Nombre / Razón social *</Label>
              <Input
                autoFocus
                value={nuevoCli.nombre}
                onChange={(e) => setNuevoCli({ ...nuevoCli, nombre: e.target.value })}
                className="bg-zinc-900 border-zinc-800"
                data-testid="input-nuevocli-nombre"
                placeholder="Ej. Perfumería Aromas S.L."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">CIF / NIF</Label>
                <Input
                  value={nuevoCli.cif}
                  onChange={(e) => setNuevoCli({ ...nuevoCli, cif: e.target.value })}
                  className="bg-zinc-900 border-zinc-800"
                  data-testid="input-nuevocli-cif"
                  placeholder="B-12345678"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Teléfono</Label>
                <Input
                  value={nuevoCli.telefono}
                  onChange={(e) => setNuevoCli({ ...nuevoCli, telefono: e.target.value })}
                  className="bg-zinc-900 border-zinc-800"
                  data-testid="input-nuevocli-telefono"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Dirección fiscal</Label>
              <Input
                value={nuevoCli.direccion_fiscal}
                onChange={(e) => setNuevoCli({ ...nuevoCli, direccion_fiscal: e.target.value })}
                className="bg-zinc-900 border-zinc-800"
                data-testid="input-nuevocli-direccion"
                placeholder="Calle, número, CP, ciudad"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Email</Label>
                <Input
                  type="email"
                  value={nuevoCli.email_contacto}
                  onChange={(e) => setNuevoCli({ ...nuevoCli, email_contacto: e.target.value })}
                  className="bg-zinc-900 border-zinc-800"
                  data-testid="input-nuevocli-email"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Persona contacto</Label>
                <Input
                  value={nuevoCli.persona_contacto}
                  onChange={(e) => setNuevoCli({ ...nuevoCli, persona_contacto: e.target.value })}
                  className="bg-zinc-900 border-zinc-800"
                  data-testid="input-nuevocli-contacto"
                />
              </div>
            </div>
            <div className="text-[11px] text-zinc-500">
              Se guardará en la ficha global de clientes y se asignará automáticamente a este presupuesto. Después podrás completar más datos desde Clientes.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenNuevoCli(false)} disabled={savingNuevoCli}>
              Cancelar
            </Button>
            <Button
              onClick={crearClienteRapido}
              disabled={savingNuevoCli || !nuevoCli.nombre.trim()}
              data-testid="btn-guardar-nuevocli"
              className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold"
            >
              {savingNuevoCli ? "Creando…" : "Crear y asignar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Dialog: Editar snapshot del cliente (sólo este presupuesto) === */}
      <Dialog open={editSnapshot} onOpenChange={setEditSnapshot}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg" data-testid="dialog-editar-snapshot">
          <DialogHeader>
            <DialogTitle className="text-amber-300 flex items-center gap-2">
              <Pencil className="w-4 h-4" /> Editar datos del cliente
            </DialogTitle>
            <div className="text-[11px] text-zinc-500">
              Los cambios afectan <b className="text-amber-300">sólo a este presupuesto</b> (no modifican la ficha global del cliente). Útil para añadir matices, una referencia interna o datos puntuales.
            </div>
          </DialogHeader>
          {snapshotDraft && (
            <div className="space-y-3 py-1 max-h-[60vh] overflow-y-auto pr-1">
              <div>
                <Label className="text-xs text-zinc-400">Razón social</Label>
                <Input value={snapshotDraft.razon_social}
                  onChange={(e) => setSnapshotDraft({ ...snapshotDraft, razon_social: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="input-snap-razon" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">CIF</Label>
                  <Input value={snapshotDraft.cif}
                    onChange={(e) => setSnapshotDraft({ ...snapshotDraft, cif: e.target.value })}
                    className="bg-zinc-900 border-zinc-800" data-testid="input-snap-cif" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Teléfono</Label>
                  <Input value={snapshotDraft.telefono}
                    onChange={(e) => setSnapshotDraft({ ...snapshotDraft, telefono: e.target.value })}
                    className="bg-zinc-900 border-zinc-800" data-testid="input-snap-telefono" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Dirección</Label>
                <Input value={snapshotDraft.direccion}
                  onChange={(e) => setSnapshotDraft({ ...snapshotDraft, direccion: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="input-snap-direccion" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">CP</Label>
                  <Input value={snapshotDraft.cp}
                    onChange={(e) => setSnapshotDraft({ ...snapshotDraft, cp: e.target.value })}
                    className="bg-zinc-900 border-zinc-800" data-testid="input-snap-cp" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Ciudad</Label>
                  <Input value={snapshotDraft.ciudad}
                    onChange={(e) => setSnapshotDraft({ ...snapshotDraft, ciudad: e.target.value })}
                    className="bg-zinc-900 border-zinc-800" data-testid="input-snap-ciudad" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Provincia</Label>
                  <Input value={snapshotDraft.provincia}
                    onChange={(e) => setSnapshotDraft({ ...snapshotDraft, provincia: e.target.value })}
                    className="bg-zinc-900 border-zinc-800" data-testid="input-snap-provincia" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Email</Label>
                  <Input type="email" value={snapshotDraft.email}
                    onChange={(e) => setSnapshotDraft({ ...snapshotDraft, email: e.target.value })}
                    className="bg-zinc-900 border-zinc-800" data-testid="input-snap-email" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Atn. (contacto)</Label>
                  <Input value={snapshotDraft.contacto_nombre}
                    onChange={(e) => setSnapshotDraft({ ...snapshotDraft, contacto_nombre: e.target.value })}
                    className="bg-zinc-900 border-zinc-800" data-testid="input-snap-atn" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-amber-300 flex items-center gap-1">
                  Observaciones / Referencia
                  <span className="text-[10px] text-zinc-500 normal-case">(p.ej. ref. interna del cliente, P.O., proyecto…)</span>
                </Label>
                <Textarea
                  value={snapshotDraft.observaciones}
                  onChange={(e) => setSnapshotDraft({ ...snapshotDraft, observaciones: e.target.value })}
                  rows={3}
                  className="bg-zinc-900 border-zinc-800"
                  data-testid="input-snap-observaciones"
                  placeholder="Ej. Ref. cliente: 2026-AROMAS-014 · Pedido a nombre de Marta R."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditSnapshot(false)}>Cancelar</Button>
            <Button onClick={guardarSnapshot}
              data-testid="btn-guardar-snapshot"
              className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold">
              <Save className="w-4 h-4 mr-1.5" /> Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Dialog: Habilitar cobro === */}
      <Dialog open={openHabilitarCobro} onOpenChange={setOpenHabilitarCobro}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="dialog-habilitar-cobro">
          <DialogHeader>
            <DialogTitle className="text-amber-300 flex items-center gap-2">
              💰 Habilitar cobro
            </DialogTitle>
            <div className="text-[11px] text-zinc-500">
              Define el importe que el cliente verá como botón de pago en su portal. Sólo se mostrará UN cobro a la vez. Cuando pague, el botón desaparece automáticamente.
            </div>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2.5 text-xs grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Total</div>
                <div className="text-zinc-200 font-semibold">{totalPres.toFixed(2)} €</div>
              </div>
              <div>
                <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Pagado</div>
                <div className="text-emerald-400 font-semibold">{yaPagado.toFixed(2)} €</div>
              </div>
              <div>
                <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Pendiente</div>
                <div className="text-amber-300 font-semibold">{pendientePres.toFixed(2)} €</div>
              </div>
            </div>

            <div>
              <Label className="text-xs text-zinc-400 mb-1.5 block">Atajos</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => sugerirCobro("anticipo_50")}
                  data-testid="btn-shortcut-anticipo"
                  className="bg-zinc-900 border-zinc-800 hover:border-amber-500/50 hover:text-amber-200 text-xs">
                  Anticipo 50%
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => sugerirCobro("total")}
                  data-testid="btn-shortcut-total"
                  className="bg-zinc-900 border-zinc-800 hover:border-amber-500/50 hover:text-amber-200 text-xs">
                  Total
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => sugerirCobro("resto")}
                  data-testid="btn-shortcut-resto"
                  className="bg-zinc-900 border-zinc-800 hover:border-amber-500/50 hover:text-amber-200 text-xs">
                  Resto
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-xs text-zinc-400">Importe a cobrar (€) *</Label>
              <Input type="number" step="0.01" min="0" max={pendientePres}
                value={cobroDraft.importe}
                onChange={(e) => setCobroDraft({ ...cobroDraft, importe: e.target.value })}
                onFocus={(e) => e.target.select()}
                className="bg-zinc-900 border-zinc-800 text-lg font-semibold"
                data-testid="input-cobro-importe"
                autoFocus
              />
            </div>

            <div>
              <Label className="text-xs text-zinc-400">Concepto / etiqueta visible en el portal</Label>
              <Input value={cobroDraft.concepto}
                onChange={(e) => setCobroDraft({ ...cobroDraft, concepto: e.target.value })}
                className="bg-zinc-900 border-zinc-800"
                data-testid="input-cobro-concepto"
                placeholder="Anticipo 50% / Resto final / Pago intermedio…"
              />
            </div>

            <div className="text-[11px] text-zinc-500 bg-amber-500/5 border border-amber-500/20 rounded p-2">
              💡 Una vez habilitado, el cliente verá un único botón en su portal. Tras el pago, la app te sugerirá habilitar el resto pendiente si lo hay.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenHabilitarCobro(false)} disabled={savingCobro}>
              Cancelar
            </Button>
            <Button onClick={habilitarCobro} disabled={savingCobro || !parseFloat(cobroDraft.importe)}
              data-testid="btn-guardar-cobro"
              className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold">
              {savingCobro ? "Habilitando…" : "Habilitar cobro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Anular y derivar ─── */}
      <Dialog open={openAnular} onOpenChange={setOpenAnular}>
        <DialogContent className="bg-zinc-950 border-fuchsia-500/40 max-w-2xl"
          data-testid="dialog-anular-derivar">
          <DialogHeader>
            <DialogTitle className="text-fuchsia-200">
              Anular y derivar a Express
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 text-xs leading-relaxed text-zinc-300">
              El presupuesto pasará a <b>“Anulado por cliente”</b> y se creará un ticket en Express con el brief técnico que indiques.
            </div>

            <div>
              <Label className="text-xs text-zinc-400">
                Brief técnico
              </Label>
              <Textarea
                value={anularBrief}
                onChange={(e) => setAnularBrief(e.target.value)}
                data-testid="anular-brief"
                rows={8}
                placeholder="Ej: - Cartel acrílico 1.5×0.6 m, vinilo blanco mate&#10;- Montaje en fachada con anclajes inox"
                className="bg-zinc-900 border-zinc-800 font-mono text-xs mt-1"
                maxLength={2000}
              />
              <div className="text-[10px] text-zinc-500 mt-1">
                {anularBrief.length}/2000 caracteres · Pre-rellenado desde los items del presupuesto.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Fecha de montaje (opcional)</Label>
                <Input
                  type="date"
                  value={anularFecha}
                  onChange={(e) => setAnularFecha(e.target.value)}
                  data-testid="anular-fecha"
                  className="bg-zinc-900 border-zinc-800 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Nota interna (opcional)</Label>
                <Input
                  value={anularMotivo}
                  onChange={(e) => setAnularMotivo(e.target.value)}
                  data-testid="anular-motivo"
                  placeholder="Nota"
                  className="bg-zinc-900 border-zinc-800 mt-1"
                  maxLength={300}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenAnular(false)} disabled={anulando}
              data-testid="btn-anular-cancel">
              Cancelar
            </Button>
            <Button onClick={confirmarAnularDerivar} disabled={anulando || !anularBrief.trim()}
              data-testid="btn-anular-confirmar"
              className="bg-fuchsia-500 hover:bg-fuchsia-600 text-zinc-950 font-semibold">
              {anulando ? "Procesando…" : "Anular y derivar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========== ITEM ROW ===========
function ItemRow({ item, onChange, onDelete, onDuplicate, onOpenCalc, onUploadImg, onOpenProveedor, fileRefImg }) {
  const cat = CATEGORIAS.find((c) => c.v === item.categoria) || CATEGORIAS[0];
  const subtotal = calcItem(item);
  const inputRef = useRef();
  fileRefImg.current[item.id] = inputRef;
  // Drag-and-drop: cualquier imagen arrastrada sobre la fila sube como imagen del ítem.
  const drop = useDropImage((file) => onUploadImg(file), { accept: ["image/"] });

  return (
    <div
      className={`bg-zinc-950/40 border rounded-lg p-3 transition-colors ${
        drop.dragging ? "border-amber-400 bg-amber-500/10 ring-2 ring-amber-400/40" : "border-zinc-800"
      }`}
      data-testid={`item-${item.id}`}
      {...drop.bind}
    >
      {drop.dragging && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-amber-300 text-xs font-semibold bg-amber-500/5 rounded-lg">
          📥 Suelta para añadir imagen
        </div>
      )}
      <div className="flex items-start gap-2 mb-2">
        <Select value={item.categoria} onValueChange={(v) => onChange({ categoria: v })}>
          <SelectTrigger className="w-32 bg-zinc-900 border-zinc-800 h-8 text-xs" data-testid={`item-cat-${item.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            {CATEGORIAS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={item.descripcion}
          onChange={(e) => onChange({ descripcion: e.target.value })}
          data-testid={`item-desc-${item.id}`}
          placeholder="Descripción que verá el cliente…"
          className="flex-1 bg-zinc-900 border-zinc-800 h-8 text-sm" />
        {onOpenCalc && (
          <Button size="sm" variant="ghost" onClick={onOpenCalc}
            title="Calculadora gráfica (m² reales, bobina, merma)"
            data-testid={`btn-calc-item-${item.id}`}
            className="h-8 w-8 p-0 text-zinc-500 hover:text-cyan-300">
            <Calculator className="w-4 h-4" />
          </Button>
        )}
        {onDuplicate && (
          <Button size="sm" variant="ghost" onClick={onDuplicate}
            title="Duplicar línea (mismo concepto, cambia solo las medidas)"
            data-testid={`btn-dup-item-${item.id}`}
            className="h-8 w-8 p-0 text-zinc-500 hover:text-amber-300">
            <Copy className="w-4 h-4" />
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onDelete}
          data-testid={`btn-del-item-${item.id}`}
          className="h-8 w-8 p-0 text-zinc-500 hover:text-red-400">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-2">
        <div>
          <Label className="text-[10px] text-zinc-500">Base (cm)</Label>
          <Input type="number" value={item.base_cm || ""} step="0.1"
            onChange={(e) => onChange({ base_cm: e.target.value ? parseFloat(e.target.value) : null })}
            onFocus={(e) => e.target.select()}
            data-testid={`item-base-${item.id}`}
            className="bg-zinc-900 border-zinc-800 h-8 text-sm" />
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">Altura (cm)</Label>
          <Input type="number" value={item.altura_cm || ""} step="0.1"
            onChange={(e) => onChange({ altura_cm: e.target.value ? parseFloat(e.target.value) : null })}
            onFocus={(e) => e.target.select()}
            data-testid={`item-alt-${item.id}`}
            className="bg-zinc-900 border-zinc-800 h-8 text-sm" />
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">Cantidad</Label>
          <Input type="number" value={item.cantidad} step="0.01"
            onChange={(e) => onChange({ cantidad: parseFloat(e.target.value) || 0 })}
            onFocus={(e) => e.target.select()}
            data-testid={`item-qty-${item.id}`}
            className="bg-zinc-900 border-zinc-800 h-8 text-sm" />
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">Unidad</Label>
          <Select value={item.unidad} onValueChange={(v) => onChange({ unidad: v })}>
            <SelectTrigger className="bg-zinc-900 border-zinc-800 h-8 text-sm" data-testid={`item-unit-${item.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              <SelectItem value="ud">ud</SelectItem>
              <SelectItem value="m2">m²</SelectItem>
              <SelectItem value="ml">ml</SelectItem>
              <SelectItem value="hora">hora</SelectItem>
              <SelectItem value="km">km</SelectItem>
              <SelectItem value="dia">día</SelectItem>
              <SelectItem value="lote">lote</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">Precio ud (€)</Label>
          <Input type="number" value={item.precio_unitario} step="0.01"
            onChange={(e) => onChange({ precio_unitario: parseFloat(e.target.value) || 0 })}
            onFocus={(e) => e.target.select()}
            data-testid={`item-price-${item.id}`}
            className="bg-zinc-900 border-zinc-800 h-8 text-sm" />
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">Descuento %</Label>
          <Input type="number" value={item.descuento_pct || 0} step="0.5"
            onChange={(e) => onChange({ descuento_pct: parseFloat(e.target.value) || 0 })}
            onFocus={(e) => e.target.select()}
            data-testid={`item-disc-${item.id}`}
            className="bg-zinc-900 border-zinc-800 h-8 text-sm" />
        </div>
      </div>

      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${cat.color}`}>{cat.l}</span>
          {/* Opcional / Grupo controls */}
          <label className="flex items-center gap-1 text-[11px] text-zinc-400 cursor-pointer hover:text-zinc-200"
            data-testid={`item-opc-${item.id}`}>
            <input type="checkbox" checked={!!item.opcional}
              onChange={(e) => onChange({ opcional: e.target.checked, ...(e.target.checked ? {} : {grupo: null}) })}
              className="accent-violet-500" />
            Opcional
          </label>
          <input type="text" value={item.grupo || ""}
            placeholder="Grupo alternativas"
            data-testid={`item-grupo-${item.id}`}
            onChange={(e) => onChange({ grupo: e.target.value || null, ...(e.target.value ? {opcional: false} : {}) })}
            className="w-32 bg-zinc-900 border border-zinc-800 rounded text-[11px] text-zinc-200 px-1.5 py-0.5 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500" />
          {item.grupo && (
            <label className="flex items-center gap-1 text-[11px] text-amber-300 cursor-pointer">
              <input type="checkbox" checked={!!item.seleccionado_default}
                onChange={(e) => onChange({ seleccionado_default: e.target.checked })}
                className="accent-amber-500" />
              Por defecto
            </label>
          )}
          {(() => {
            const m2 = superficieM2(item);
            const qty = parseFloat(item.cantidad) || 0;
            if (item.unidad === "m2" && m2) {
              return (
                <span className="text-[11px] text-emerald-300 bg-emerald-900/20 border border-emerald-500/30 px-1.5 py-0.5 rounded font-mono">
                  {m2.toFixed(2)} m²{qty !== 1 ? ` × ${qty}` : ""}
                </span>
              );
            }
            if (item.unidad === "ml" && item.base_cm) {
              const ml = parseFloat(item.base_cm) / 100;
              return (
                <span className="text-[11px] text-emerald-300 bg-emerald-900/20 border border-emerald-500/30 px-1.5 py-0.5 rounded font-mono">
                  {ml.toFixed(2)} ml{qty !== 1 ? ` × ${qty}` : ""}
                </span>
              );
            }
            return null;
          })()}
          {item.proveedor_externo?.nombre && (
            <button onClick={onOpenProveedor} data-testid={`btn-prov-${item.id}`}
              className="text-[11px] px-2 py-0.5 rounded bg-orange-600/20 text-orange-200 border border-orange-500/40 hover:bg-orange-600/30">
              Proveedor: {item.proveedor_externo.nombre} {item.proveedor_externo.numero_presupuesto ? `· ${item.proveedor_externo.numero_presupuesto}` : ""}
            </button>
          )}
          {item.categoria === "externo" && !item.proveedor_externo?.nombre && (
            <button onClick={onOpenProveedor} data-testid={`btn-add-prov-${item.id}`}
              className="text-[11px] px-2 py-0.5 rounded bg-orange-600/15 text-orange-300 border border-orange-500/30 hover:bg-orange-600/25">
              + Datos proveedor
            </button>
          )}
          {item.imagen_url ? (
            <a href={item.imagen_url} target="_blank" rel="noreferrer"
              className="text-[11px] text-amber-300 hover:underline flex items-center gap-1">
              <ImageIcon className="w-3 h-3" /> Imagen
            </a>
          ) : (
            <button onClick={() => inputRef.current?.click()} data-testid={`btn-add-img-${item.id}`}
              className="text-[11px] text-zinc-400 hover:text-amber-300 flex items-center gap-1">
              <ImageIcon className="w-3 h-3" /> + Imagen
            </button>
          )}
          <input ref={inputRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => { onUploadImg(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        <div className="text-amber-300 font-bold text-base">{fmtEUR(subtotal)}</div>
      </div>
    </div>
  );
}

// =========== CATALOGO PICKER (buscador inteligente) ===========
// Normaliza texto: minúsculas, sin tildes, sin signos.
function normTxt(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Resalta términos en un texto (multipalabra, case/acento-insensible)
function Highlight({ text, terms }) {
  if (!text) return null;
  if (!terms || !terms.length) return <>{text}</>;
  // Construye regex a partir de los términos sobre el texto original
  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).filter(Boolean);
  if (!escaped.length) return <>{text}</>;
  // Match insensible a tildes: usamos un acent-fold sobre el texto y trabajamos por posiciones
  const folded = String(text).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const re = new RegExp(`(${escaped.join("|")})`, "gi");
  const ranges = [];
  let m;
  while ((m = re.exec(folded)) !== null) {
    ranges.push([m.index, m.index + m[0].length]);
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  if (!ranges.length) return <>{text}</>;
  // Merge solapados
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [s, e] of ranges) {
    if (merged.length && s <= merged[merged.length - 1][1]) {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    } else {
      merged.push([s, e]);
    }
  }
  const parts = [];
  let cursor = 0;
  merged.forEach(([s, e], i) => {
    if (cursor < s) parts.push(<span key={`n-${i}`}>{text.slice(cursor, s)}</span>);
    parts.push(<mark key={`h-${i}`} className="bg-amber-400/30 text-amber-100 rounded px-0.5">{text.slice(s, e)}</mark>);
    cursor = e;
  });
  if (cursor < text.length) parts.push(<span key="tail">{text.slice(cursor)}</span>);
  return <>{parts}</>;
}

function CatalogoPicker({ catalogo, onPick, onLibre, onExterno }) {
  const [q, setQ] = useState("");
  const [filtroCat, setFiltroCat] = useState("todas");
  const inputRef = useRef(null);

  // Atajo: enfoca el input al abrir
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, []);

  // Contadores por categoría (sobre catálogo activo)
  const counters = useMemo(() => {
    const out = { todas: 0 };
    (catalogo || []).forEach((c) => {
      if (!c.activo) return;
      out.todas += 1;
      out[c.categoria] = (out[c.categoria] || 0) + 1;
    });
    return out;
  }, [catalogo]);

  // Búsqueda multipalabra + scoring por relevancia
  const filtrados = useMemo(() => {
    const tokens = normTxt(q).split(" ").filter(Boolean);
    const arr = (catalogo || []).filter((c) => {
      if (!c.activo) return false;
      if (filtroCat !== "todas" && c.categoria !== filtroCat) return false;
      if (!tokens.length) return true;
      const hayNom = normTxt(c.nombre);
      const hayDesc = normTxt(c.descripcion);
      const hayCat = normTxt(c.categoria);
      const hayUni = normTxt(c.unidad);
      // TODOS los tokens deben matchear en alguno de los campos
      return tokens.every(
        (t) =>
          hayNom.includes(t) || hayDesc.includes(t) || hayCat.includes(t) || hayUni.includes(t),
      );
    });
    if (!tokens.length) return arr;
    // Scoring: matches en nombre > descripción > categoría; bonus si empieza por
    const scored = arr.map((c) => {
      const hayNom = normTxt(c.nombre);
      const hayDesc = normTxt(c.descripcion);
      const hayCat = normTxt(c.categoria);
      let score = 0;
      tokens.forEach((t) => {
        if (hayNom.startsWith(t)) score += 100;
        else if (hayNom.includes(` ${t}`)) score += 60;
        else if (hayNom.includes(t)) score += 40;
        else if (hayDesc.includes(t)) score += 15;
        else if (hayCat.includes(t)) score += 5;
      });
      return { c, score };
    });
    scored.sort((a, b) => b.score - a.score || (a.c.orden || 0) - (b.c.orden || 0));
    return scored.map((x) => x.c);
  }, [catalogo, q, filtroCat]);

  const tokens = normTxt(q).split(" ").filter(Boolean);

  return (
    <div className="space-y-3">
      {/* Buscador principal con icono y atajo "/" */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-500 pointer-events-none" />
        <Input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre, descripción o categoría…  ej. ' vinilo blanco 3m '"
          data-testid="input-buscar-catalogo"
          className="pl-9 pr-20 bg-zinc-900 border-zinc-800 h-10 text-sm"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              if (q) { setQ(""); e.stopPropagation(); }
            }
            // Enter: si solo hay un resultado, lo añade directo
            if (e.key === "Enter" && filtrados.length === 1) {
              onPick(filtrados[0]);
            }
          }}
        />
        {q && (
          <button
            onClick={() => setQ("")}
            data-testid="btn-clear-buscar"
            className="absolute right-2 top-1.5 text-[10px] text-zinc-500 hover:text-amber-300 border border-zinc-700 rounded px-1.5 py-0.5"
            title="Limpiar (Esc)"
          >
            Esc
          </button>
        )}
      </div>

      {/* Chips de categoría con contadores */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setFiltroCat("todas")}
          data-testid="chip-cat-todas"
          className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
            filtroCat === "todas"
              ? "bg-amber-500/25 text-amber-100 border-amber-400/50"
              : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
          }`}
        >
          Todas <span className="opacity-60">· {counters.todas || 0}</span>
        </button>
        {CATEGORIAS.map((c) => {
          const n = counters[c.v] || 0;
          if (!n && filtroCat !== c.v) return null;
          const active = filtroCat === c.v;
          return (
            <button
              key={c.v}
              onClick={() => setFiltroCat(active ? "todas" : c.v)}
              data-testid={`chip-cat-${c.v}`}
              className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                active
                  ? `${c.color} border-current`
                  : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
              }`}
            >
              {c.l} <span className="opacity-60">· {n}</span>
            </button>
          );
        })}
      </div>

      {/* Botones rápidos: items libres / externos */}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" onClick={() => onLibre("produccion")} data-testid="btn-item-libre"
          className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 h-7 text-[11px]">
          + Item libre (sin catálogo)
        </Button>
        <Button size="sm" onClick={onExterno} data-testid="btn-item-externo"
          className="bg-orange-600/30 hover:bg-orange-600/50 text-orange-100 border border-orange-500/40 h-7 text-[11px]">
          + Item externo (con proveedor)
        </Button>
        <div className="ml-auto text-[11px] text-zinc-500 self-center">
          {filtrados.length} resultado{filtrados.length === 1 ? "" : "s"}
          {q && <span className="opacity-70"> · pulsa <kbd className="px-1 py-0.5 bg-zinc-900 border border-zinc-800 rounded text-[10px]">Enter</kbd> si solo queda 1</span>}
        </div>
      </div>

      <div className="max-h-[420px] overflow-y-auto border border-zinc-800 rounded bg-zinc-950/50">
        {filtrados.length === 0 && (
          <div className="p-6 text-center text-zinc-500 text-sm italic">
            {(catalogo || []).length === 0
              ? "Catálogo vacío. Pídele al admin que ejecute el seed de Logomedia."
              : q
                ? <>Sin resultados para <b className="text-zinc-300">"{q}"</b>. Prueba con menos palabras o sin tildes.</>
                : "Sin resultados."}
          </div>
        )}
        {filtrados.map((c) => {
          const cat = CATEGORIAS.find((x) => x.v === c.categoria) || CATEGORIAS[0];
          return (
            <button key={c.id} onClick={() => onPick(c)}
              data-testid={`pick-cat-${c.id}`}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-900 border-b border-zinc-800/60 last:border-0 text-left">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${cat.color} flex-shrink-0`}>{cat.l}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 truncate">
                  <Highlight text={c.nombre} terms={tokens} />
                </div>
                {c.descripcion && (
                  <div className="text-[10px] text-zinc-500 truncate">
                    <Highlight text={c.descripcion} terms={tokens} />
                  </div>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-amber-300 font-bold text-sm">{fmtEUR(c.precio)}</div>
                <div className="text-[10px] text-zinc-500">/{c.unidad}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =========== PROVEEDOR DIALOG ===========
function ProveedorDialog({ item, onClose, onSave, onUploadAdjunto }) {
  const fileRef = useRef();
  const [prov, setProv] = useState(item?.proveedor_externo || {});

  useEffect(() => { setProv(item?.proveedor_externo || {}); }, [item?.id]);

  if (!item) return null;

  const precioVenta = (parseFloat(prov.precio_coste) || 0) * (1 + (parseFloat(prov.margen_pct) || 0) / 100);

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl" data-testid="dialog-proveedor">
        <DialogHeader>
          <DialogTitle className="text-orange-300">Datos del proveedor externo (interno, NO sale al cliente)</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Nombre proveedor</Label>
              <Input value={prov.nombre || ""}
                onChange={(e) => setProv({ ...prov, nombre: e.target.value })}
                data-testid="prov-nombre"
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Nº presupuesto proveedor</Label>
              <Input value={prov.numero_presupuesto || ""}
                onChange={(e) => setProv({ ...prov, numero_presupuesto: e.target.value })}
                data-testid="prov-numero"
                placeholder="Ej: PR-2401"
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Precio coste (€)</Label>
              <Input type="number" step="0.01" value={prov.precio_coste || 0}
                onChange={(e) => setProv({ ...prov, precio_coste: parseFloat(e.target.value) || 0 })}
                onFocus={(e) => e.target.select()}
                data-testid="prov-coste"
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Margen %</Label>
              <Input type="number" step="0.5" value={prov.margen_pct || 0}
                onChange={(e) => setProv({ ...prov, margen_pct: parseFloat(e.target.value) || 0 })}
                onFocus={(e) => e.target.select()}
                data-testid="prov-margen"
                className="bg-zinc-900 border-zinc-800" />
            </div>
          </div>

          <div className="bg-orange-900/20 border border-orange-500/30 rounded p-3 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-orange-200">Precio venta calculado:</span>
              <span className="text-orange-100 font-bold text-lg">{fmtEUR(precioVenta)}</span>
            </div>
            <div className="text-[11px] text-zinc-400 mt-1">
              Se aplicará como precio unitario del item. Ajusta el margen hasta que te encaje el resultado.
            </div>
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Notas internas</Label>
            <Textarea value={prov.notas || ""}
              onChange={(e) => setProv({ ...prov, notas: e.target.value })}
              data-testid="prov-notas"
              placeholder="Ej: confirmar plazo de entrega antes de aceptar."
              className="bg-zinc-900 border-zinc-800 min-h-[60px]" />
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Presupuesto del proveedor (PDF, interno)</Label>
            {prov.adjunto_url ? (
              <div className="mt-2 flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded p-2">
                <FileText className="w-4 h-4 text-orange-300" />
                <a href={prov.adjunto_url} target="_blank" rel="noreferrer"
                  className="flex-1 text-sm text-zinc-200 hover:text-orange-300 truncate">
                  {prov.adjunto_nombre || "Adjunto"}
                </a>
                <ExternalLink className="w-3.5 h-3.5 text-zinc-500" />
              </div>
            ) : (
              <Button size="sm" onClick={() => fileRef.current?.click()}
                data-testid="prov-upload"
                className="mt-2 bg-zinc-800 hover:bg-zinc-700">
                <Paperclip className="w-3.5 h-3.5 mr-1" /> Subir PDF proveedor
              </Button>
            )}
            <input ref={fileRef} type="file" className="hidden"
              accept="application/pdf,image/*"
              onChange={(e) => { onUploadAdjunto(e.target.files?.[0]); e.target.value = ""; }} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="prov-cancel">Cancelar</Button>
          <Button onClick={() => onSave({
            proveedor_externo: prov,
            precio_unitario: precioVenta,
          })}
            data-testid="prov-save"
            className="bg-orange-500 hover:bg-orange-600 text-zinc-950 font-semibold">
            Aplicar y cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =========== CALCULADORA GRÁFICA DIALOG ===========
// Mini-ventana que envuelve CalcMaterialPanel (la calculadora "real" de m² con
// bobina + merma + rotación) y permite aplicar el resultado al ítem del presupuesto.
function CalcGraficaDialog({ item, materiales, onClose, onApply }) {
  const [matId, setMatId] = useState(null);
  const [qBuscar, setQBuscar] = useState("");

  // Materiales con unidad m² (los únicos válidos para esta calculadora)
  const matsM2 = useMemo(
    () => (materiales || []).filter((m) => m.unidad === "m²" || m.unidad === "m2"),
    [materiales],
  );

  // Material filtrado por búsqueda
  const matsFiltrados = useMemo(() => {
    if (!qBuscar) return matsM2;
    const t = qBuscar
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    return matsM2.filter((m) => {
      const n = (m.nombre || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      return n.includes(t);
    });
  }, [matsM2, qBuscar]);

  // Reset al abrir/cerrar
  useEffect(() => {
    if (item) {
      setMatId(null);
      setQBuscar("");
    }
  }, [item?.id]);

  if (!item) return null;

  // Aplicar resultado: actualiza dimensiones (ya las tenía) + cantidad efectiva en m² + precio_unitario
  // Estrategia: usamos m² reales como `cantidad` y unidad `m2`, y precio_unitario = precio_total / m²_reales
  // Así el ítem refleja exactamente lo que va a salir en el presupuesto.
  const handleApply = (calc) => {
    const m2_reales = parseFloat(calc.m2_reales) || 0;
    const precioTotal = parseFloat(calc.precio_total) || 0;
    const precioUnit = m2_reales > 0 ? +(precioTotal / m2_reales).toFixed(2) : (parseFloat(item.precio_unitario) || 0);
    onApply({
      unidad: "m2",
      base_cm: parseFloat(item.base_cm) || null,
      altura_cm: parseFloat(item.altura_cm) || null,
      cantidad: m2_reales,
      precio_unitario: precioUnit,
      // Anotación interna invisible al cliente (para auditoría)
      calc_grafica: {
        material_id: matId,
        m2_teoricos: calc.m2_teoricos,
        m2_reales: calc.m2_reales,
        desperdicio_pct: calc.desperdicio_pct,
        coste_total: calc.coste_total,
        precio_total: calc.precio_total,
        applied_at: new Date().toISOString(),
      },
    });
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-calc-grafica">
        <DialogHeader>
          <DialogTitle className="text-cyan-300 flex items-center gap-2">
            <Calculator className="w-5 h-5" /> Calculadora gráfica · merma y aprovechamiento
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="bg-cyan-900/15 border border-cyan-500/30 rounded p-3 text-xs text-cyan-100">
            <b>Cómo usarla</b>: elige el material, rellena dimensiones base × altura × cantidad,
            y la calculadora te dice cuántos m² <b>reales</b> necesitas (con bobina, merma de
            corte, rotación de paños y solape). Útil para comparar materiales y elegir el más
            rentable antes de presupuestar.
          </div>

          {/* Resumen del item activo */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2.5 text-xs grid grid-cols-4 gap-3">
            <div>
              <div className="text-[10px] text-zinc-500 uppercase">Concepto</div>
              <div className="text-zinc-100 truncate">{item.descripcion || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] text-zinc-500 uppercase">Base</div>
              <div className="text-zinc-100 font-mono">{item.base_cm || "—"} cm</div>
            </div>
            <div>
              <div className="text-[10px] text-zinc-500 uppercase">Altura</div>
              <div className="text-zinc-100 font-mono">{item.altura_cm || "—"} cm</div>
            </div>
            <div>
              <div className="text-[10px] text-zinc-500 uppercase">Cantidad</div>
              <div className="text-zinc-100 font-mono">{item.cantidad || 0}</div>
            </div>
          </div>

          {/* Selector de material */}
          <div>
            <Label className="text-xs text-zinc-400">Material (catálogo m²)</Label>
            <Input
              value={qBuscar}
              onChange={(e) => setQBuscar(e.target.value)}
              placeholder="Buscar material por nombre…"
              data-testid="calc-buscar-material"
              className="bg-zinc-900 border-zinc-800 h-8 text-sm mt-1"
            />
            <div className="max-h-40 overflow-y-auto mt-1 border border-zinc-800 rounded bg-zinc-950/50">
              {matsFiltrados.length === 0 && (
                <div className="p-3 text-center text-zinc-500 text-xs italic">
                  {matsM2.length === 0
                    ? "No hay materiales m² en el catálogo. Ve a Materiales y dales de alta."
                    : "Sin resultados."}
                </div>
              )}
              {matsFiltrados.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMatId(m.id)}
                  data-testid={`calc-pick-material-${m.id}`}
                  className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-left text-xs border-b border-zinc-800/60 last:border-0 transition-colors ${
                    matId === m.id ? "bg-cyan-500/15 text-cyan-100" : "text-zinc-300 hover:bg-zinc-900"
                  }`}
                >
                  <span className="truncate">{m.nombre}</span>
                  <span className="text-[10px] text-zinc-500 flex-shrink-0">
                    bobina {m.ancho_bobina_cm || m.ancho_imprimible_cm || "—"}cm · {fmtEUR(m.precio_venta || 0)}/m²
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Panel de cálculo: reutilizamos el componente existente */}
          {matId ? (
            <CalcMaterialPanel
              materialId={matId}
              materiales={matsM2}
              ancho_cm={item.base_cm || ""}
              alto_cm={item.altura_cm || ""}
              cantidad={item.cantidad || 1}
              onApply={handleApply}
            />
          ) : (
            <div className="bg-zinc-900/40 border border-dashed border-zinc-700 rounded p-4 text-center text-xs text-zinc-500 italic">
              Selecciona un material para activar el cálculo.
            </div>
          )}

          {(!item.base_cm || !item.altura_cm) && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 text-[11px] text-amber-200">
              ⚠️ Este ítem no tiene <b>base</b> y/o <b>altura</b>. Rellénalas en la fila del presupuesto
              antes de abrir la calculadora (o ciérrala, ajusta y vuelve a abrirla).
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="calc-grafica-cancel">
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
